// src/components/Pendenzen.jsx
// Tab "Pendenzen" der Projekt-Detailansicht: mehrspaltige Tabelle für Pendenzen,
// Entscheide und Notizen mit Erfassungsformular, Auto-Nummerierung, Filter & Suche.
// US-PEND-001: Erfassung + Liste.
// US-PEND-002: Bearbeiten-Modal (alle Felder), Verantwortliche als Mehrfachauswahl
//              aus projectPersons, Status-/erledigtAm-Logik, Löschen mit Bestätigung.
import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  getPendenzen,
  createPendenz,
  updatePendenz,
  deletePendenz,
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  FIXED_CATEGORIES,
} from "../services/azurePendenzenService";
import PendenzenBoard, { PendenzCard } from "./PendenzenBoard";
import { getAllPartners } from "../services/azurePartnersService";
import { getAllPersons } from "../services/azurePersonsService";
import { getProjectRelations } from "../services/azureRelationsService";
import { getAllTeamMembers } from "../services/azureTeamService";
import {
  getSharesForProject,
  createShare,
  revokeShare,
  isShareValid,
} from "../services/azurePendenzenSharesService";
import { useLocation } from "react-router-dom";
import { getProjectAssignments } from "../services/azureProjectAssignmentService";
import { exportPendenzenToExcel } from "../utils/exportPendenzen";
import PendenzResponsibleCreateModal from "./PendenzResponsibleCreateModal";
import PendenzResponsibleLinkModal from "./PendenzResponsibleLinkModal";
import PendenzMembersModal from "./PendenzMembersModal";
import SitzungQuickCreateModal from "./SitzungQuickCreateModal";
import { AttendeeSelect, ProtokollModal } from "./Sitzungen";
import {
  getSitzungen,
  createSitzung,
  updateSitzung,
  deleteSitzung,
  suggestNextNr,
  SITZUNG_ARTEN,
} from "../services/azureSitzungenService";
import { getPlansWithVersions } from "../services/azurePlansService";
import Plans from "./Plans.jsx";
import { getMarkupsForProject, createMarkup } from "../services/azurePendenzenMarkupsService";
import PlanViewer from "./PlanViewer";
import SitzungPraesentation from "./SitzungPraesentation";
import { pruneUnusedFolien } from "../services/azureSitzungFolienService";
import RichTextEditor from "./RichTextEditor";
import { sanitizeRichText, richTextToPlainText, isRichTextEmpty } from "../utils/richText";
import { useAuth } from "../hooks/useAuth";
import { hasRole, ROLES, canEditProjectAssigned } from "../utils/permissions";
import "../styles/pendenzen.scss";

// Feste Verantwortlich-Option "Malheur&Fortuna" (allgemein)
const MF_OPTION = { id: "mf:general", label: "Malheur&Fortuna", group: "Allgemein" };

// Verantwortlich-Optionen aufbauen: Malheur&Fortuna + Organisationen & Personen am
// Projekt (Partner/Personen über Relations) + aktive interne Team-Mitglieder. IDs mit
// Präfix, damit die Quellen kollisionsfrei in verantwortlichIds gespeichert werden.
export const buildResponsibleOptions = (partners, persons, relations, teamMembers) => {
  const options = [MF_OPTION];

  // Am Projekt verknüpfte Organisationen und Personen (Relations, beide Richtungen)
  const orgIds = new Set();
  const personIds = new Set();
  (relations || []).forEach((r) => {
    if (r.targetType === "partner") orgIds.add(r.targetId);
    if (r.sourceType === "partner") orgIds.add(r.sourceId);
    if (r.targetType === "person") personIds.add(r.targetId);
    if (r.sourceType === "person") personIds.add(r.sourceId);
  });
  (partners || [])
    .filter((p) => orgIds.has(p.id))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
    .forEach((p) =>
      options.push({ id: `org:${p.id}`, label: p.name, group: "Organisation" })
    );
  (persons || [])
    .filter((p) => personIds.has(p.id))
    .map((p) => ({
      id: `person:${p.id}`,
      label:
        `${p.firstName || ""} ${p.lastName || ""}`.trim() ||
        p.email ||
        "(ohne Name)",
      group: "Person",
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
    .forEach((o) => options.push(o));

  // Aktive interne Team-Mitglieder (kein:e Freelancer:in, aktuell angestellt)
  const today = new Date().toISOString().slice(0, 10);
  (teamMembers || [])
    .filter(
      (m) =>
        m.employeeUuid &&
        m.imTeam !== false &&
        m.kategorie !== "Freelancer" &&
        (!m.start || m.start <= today) &&
        (!m.ende || m.ende >= today)
    )
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
    .forEach((m) =>
      options.push({ id: `team:${m.employeeUuid}`, label: m.name, group: "Team" })
    );

  return options;
};

// Art-Definitionen (P/E/N) als farbige Badges
const ART_OPTIONS = [
  { value: "P", label: "Pendenz" },
  { value: "E", label: "Entscheid" },
  { value: "N", label: "Notiz" },
];

// Kategorien = fixe Standardkategorien + projektspezifische (siehe Service).
// Werden im Board als Spalten verwendet.

// Status-Definitionen (offen ist der Initialzustand neuer Einträge)
const STATUS_OPTIONS = [
  { value: "offen", label: "Offen" },
  { value: "inBearbeitung", label: "In Bearbeitung" },
  { value: "kritisch", label: "Kritisch" },
  { value: "erledigt", label: "Erledigt" },
];

const artLabel = (value) =>
  ART_OPTIONS.find((o) => o.value === value)?.label || value;

// Interner Verantwortliche-Anzeigetext aus den ausgewählten Optionen-IDs ableiten.
// Team-Mitglieder handeln für die Firma und werden intern als
// "Malheur&Fortuna (Name)" gezeigt (Firma + Person). Organisationen/Personen mit
// ihrem Namen, die allgemeine MF-Option als "Malheur&Fortuna".
export const verantwortlichDisplay = (ids, options) =>
  (ids || [])
    .map((id) => {
      const o = options.find((x) => x.id === id);
      if (!o) return null;
      return o.group === "Team" ? `Malheur&Fortuna (${o.label})` : o.label;
    })
    .filter(Boolean)
    .join(", ");

// Externer Verantwortliche-Anzeigetext (Read-only-Share): Namen einzelner
// Team-Mitglieder werden bewusst NICHT nach aussen gezeigt – sie erscheinen nur
// als "Malheur&Fortuna" (dedupliziert). Organisationen/Personen wie intern.
export const verantwortlichDisplayExtern = (ids, options) => {
  const parts = (ids || [])
    .map((id) => {
      const o = options.find((x) => x.id === id);
      if (!o) return null;
      return o.group === "Team" || o.id === "mf:general" ? "Malheur&Fortuna" : o.label;
    })
    .filter(Boolean);
  return Array.from(new Set(parts)).join(", ");
};

// Verantwortlich-String berechnen: bei Auswahl aus Labels ableiten, sonst den
// (ggf. frei erfassten Alt-)Text aus US-PEND-001 beibehalten.
export const computeVerantwortlich = (ids, options, fallback) =>
  ids && ids.length ? verantwortlichDisplay(ids, options) : fallback || "";

// Wie computeVerantwortlich, aber für die externe Ansicht (Team-Namen verborgen).
export const computeVerantwortlichExtern = (ids, options, fallback) =>
  ids && ids.length ? verantwortlichDisplayExtern(ids, options) : fallback || "";

// Heutiges Datum als ISO "YYYY-MM-DD" (lokal)
const todayIso = () => {
  const d = new Date();
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d - tzOffset).toISOString().slice(0, 10);
};

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("de-CH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

// Massgeblicher Termin = Termin neu, falls gesetzt, sonst Termin soll
const massgeblicherTermin = (p) => p.terminNeu || p.terminSoll || "";

// Überfällig = nicht erledigter Eintrag mit massgeblichem Termin in der Vergangenheit
const istUeberfaellig = (p) => {
  if (p.status === "erledigt") return false;
  const termin = massgeblicherTermin(p);
  if (!termin) return false;
  return termin < todayIso();
};

export const emptyForm = () => ({
  typ: "P",
  kategorie: "Org",
  beschreibung: "",
  verantwortlichIds: [],
  terminSoll: "",
  terminNeu: "",
  status: "offen",
  kommentar: "",
  checkliste: [],
  // Freie Themen-Tags (Mehrfach), z.B. Brandschutz/Statik/Behörden
  themen: [],
  // Extern teilen ist standardmässig aktiviert (Nutzerwunsch)
  sichtbarExtern: true,
  // Optionale Zuordnung zu einem Sitzungsgefäss (US-SITZ-001)
  sitzungId: "",
  // Optionale Zuordnung zu einer Folie/einem Zwischenraum (US-SITZ-003)
  praesentationAnchor: "",
});

// --- Mehrfachauswahl der Verantwortlichen (MF / Organisationen / Team) -----
const GROUP_ORDER = ["Allgemein", "Organisation", "Person", "Team"];

// Exportiert, damit SitzungPraesentation.jsx (US-SITZ-003) dieselbe
// Verantwortliche-Auswahl für die folienbezogene Schnellerfassung nutzt.
export const ResponsibleSelect = ({
  options,
  selectedIds,
  onChange,
  onCreateRequest,
  crmOptions = [],
  onLinkExisting,
  // Wird nach einer Auswahl im Einzelmodus aufgerufen, damit der Aufrufer zum
  // nächsten Feld springen kann (Nutzerwunsch: nach einer Zuständigkeit weiter).
  onCommit,
  // singleOnly: keine "Mehrere zulassen"-Option (z.B. Schnellerfassung).
  singleOnly = false,
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  // Standard: genau eine:n Verantwortliche:n. Mehrere nur, wenn aktiv erlaubt
  // (oder wenn beim Öffnen bereits mehrere gesetzt sind, z.B. Bearbeiten).
  const [allowMultiple, setAllowMultiple] = useState(
    !singleOnly && (selectedIds || []).length > 1
  );
  const inputRef = useRef(null);
  // Dropdown fixed positionieren, damit es NICHT vom überlaufenden Container
  // (z.B. Tabellen-Wrapper der Schnellerfassung) abgeschnitten wird.
  const wrapRef = useRef(null);
  const [dropPos, setDropPos] = useState(null);
  useLayoutEffect(() => {
    if (!open) {
      setDropPos(null);
      return undefined;
    }
    const measure = () => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setDropPos({ top: r.bottom + 2, left: r.left, width: r.width });
    };
    measure();
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [open]);

  const selected = useMemo(
    () =>
      (selectedIds || [])
        .map((id) => options.find((o) => o.id === id))
        .filter(Boolean),
    [options, selectedIds]
  );

  // Verfügbare Optionen nach Gruppe gegliedert (für die Dropdown-Abschnitte)
  const groupedAvailable = useMemo(() => {
    const q = query.trim().toLowerCase();
    const avail = options
      .filter((o) => !selectedIds.includes(o.id))
      .filter((o) => (q ? o.label.toLowerCase().includes(q) : true));
    return GROUP_ORDER.map((group) => ({
      group,
      items: avail.filter((o) => o.group === group),
    })).filter((g) => g.items.length > 0);
  }, [options, selectedIds, query]);

  const add = (id) => {
    if (allowMultiple) {
      onChange([...selectedIds, id]);
      setQuery("");
      return;
    }
    // Einzelmodus: ersetzt die Auswahl, schliesst und springt zum nächsten Feld.
    // WICHTIG: kein input.blur() – das würde in der Schnellerfassung sofort den
    // Zeilen-Commit auslösen (noch vor dem onChange-State-Update → Verantwortliche
    // ginge verloren). Der Fokus wird über onCommit weitergereicht.
    onChange([id]);
    setQuery("");
    setOpen(false);
    onCommit?.();
  };
  const remove = (id) => onChange(selectedIds.filter((x) => x !== id));

  // Im CRM vorhandene, aber noch nicht dem Projekt zugeordnete Einträge, die zur
  // Sucheingabe passen (nur beim Tippen, um nicht das ganze CRM aufzulisten).
  const crmAvailable = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return crmOptions
      .filter((o) => !selectedIds.includes(o.id))
      .filter((o) => o.label.toLowerCase().includes(q))
      .slice(0, 20);
  }, [crmOptions, selectedIds, query]);

  // Neue Verantwortliche (Organisation/Person) über das CRM-Modal anlegen und
  // direkt auswählen. addFn hängt die neue ID an die aktuelle Auswahl an.
  const requestCreate = (createType) => {
    const name = query.trim();
    setQuery("");
    setOpen(false);
    onCreateRequest?.(createType, name, (newId) =>
      onChange([...selectedIds, newId])
    );
  };

  // Bestehenden CRM-Eintrag mit dem Projekt verbinden (kein neuer Eintrag) und
  // direkt auswählen.
  const requestLink = (o) => {
    setQuery("");
    setOpen(false);
    onLinkExisting?.(o, (newId) => onChange([...selectedIds, newId]));
  };

  // Flache Liste aller anwählbaren Einträge (in Anzeige-Reihenfolge) für die
  // Pfeiltasten-Navigation im Dropdown.
  const flatEntries = useMemo(() => {
    const e = [];
    groupedAvailable.forEach((g) =>
      g.items.forEach((o) => e.push({ kind: "add", id: o.id, key: `add:${o.id}` }))
    );
    crmAvailable.forEach((o) => e.push({ kind: "link", o, key: `link:${o.id}` }));
    if (query.trim() && onCreateRequest) {
      e.push({ kind: "create", createType: "organization", key: "create:org" });
      e.push({ kind: "create", createType: "person", key: "create:person" });
    }
    return e;
  }, [groupedAvailable, crmAvailable, query, onCreateRequest]);

  const [activeIndex, setActiveIndex] = useState(0);
  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);
  const activeKey = flatEntries[activeIndex]?.key;

  const executeEntry = (entry) => {
    if (!entry) return;
    if (entry.kind === "add") add(entry.id);
    else if (entry.kind === "link") requestLink(entry.o);
    else if (entry.kind === "create") requestCreate(entry.createType);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.min(flatEntries.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      // Nur wenn ein Dropdown-Eintrag aktiv ist: diesen wählen und das Ereignis
      // stoppen, damit z.B. der Zeilen-Commit der Schnellerfassung nicht feuert.
      if (open && flatEntries[activeIndex]) {
        e.preventDefault();
        e.stopPropagation();
        executeEntry(flatEntries[activeIndex]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="person-multiselect">
      {selected.length > 0 && (
        <div className="person-multiselect__chips">
          {selected.map((o) => (
            <span key={o.id} className="person-chip">
              {o.label}
              <button
                type="button"
                onClick={() => remove(o.id)}
                aria-label={`${o.label} entfernen`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      {(allowMultiple || selected.length === 0) && (
      <div className="person-multiselect__input-wrap" ref={wrapRef}>
        <input
          ref={inputRef}
          type="text"
          value={query}
          placeholder={
            allowMultiple
              ? "Weitere Verantwortliche suchen / hinzufügen…"
              : "Verantwortliche:n suchen / auswählen…"
          }
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={handleInputKeyDown}
        />
        {open &&
          (groupedAvailable.length > 0 ||
            crmAvailable.length > 0 ||
            query.trim()) && (
          <ul
            className="person-multiselect__dropdown"
            style={
              dropPos
                ? {
                    position: "fixed",
                    top: dropPos.top,
                    left: dropPos.left,
                    minWidth: dropPos.width,
                    width: "max-content",
                    maxWidth: "min(90vw, 520px)",
                    right: "auto",
                  }
                : undefined
            }
          >
            {groupedAvailable.map((g) => (
              <React.Fragment key={g.group}>
                <li className="person-multiselect__group">{g.group}</li>
                {g.items.map((o) => (
                  <li key={o.id}>
                    <button
                      type="button"
                      className={activeKey === `add:${o.id}` ? "is-active" : undefined}
                      onMouseEnter={() =>
                        setActiveIndex(
                          flatEntries.findIndex((x) => x.key === `add:${o.id}`)
                        )
                      }
                      onMouseDown={(e) => {
                        e.preventDefault();
                        add(o.id);
                      }}
                    >
                      <span>{o.label}</span>
                    </button>
                  </li>
                ))}
              </React.Fragment>
            ))}

            {crmAvailable.length > 0 && (
              <React.Fragment>
                <li className="person-multiselect__group">
                  Im CRM (noch nicht zugeordnet)
                </li>
                {crmAvailable.map((o) => (
                  <li key={o.id}>
                    <button
                      type="button"
                      className={activeKey === `link:${o.id}` ? "is-active" : undefined}
                      onMouseEnter={() =>
                        setActiveIndex(
                          flatEntries.findIndex((x) => x.key === `link:${o.id}`)
                        )
                      }
                      onMouseDown={(e) => {
                        e.preventDefault();
                        requestLink(o);
                      }}
                    >
                      <span>{o.label}</span>
                      <span className="person-org">{o.group}</span>
                    </button>
                  </li>
                ))}
              </React.Fragment>
            )}

            {query.trim() && onCreateRequest && (
              <React.Fragment>
                <li className="person-multiselect__group">Neu anlegen</li>
                <li>
                  <button
                    type="button"
                    className={`person-multiselect__create${
                      activeKey === "create:org" ? " is-active" : ""
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      requestCreate("organization");
                    }}
                  >
                    ＋ „{query.trim()}" als Organisation anlegen
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className={`person-multiselect__create${
                      activeKey === "create:person" ? " is-active" : ""
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      requestCreate("person");
                    }}
                  >
                    ＋ „{query.trim()}" als Person anlegen
                  </button>
                </li>
              </React.Fragment>
            )}
          </ul>
        )}
      </div>
      )}

      {singleOnly ? null : !allowMultiple ? (
        <button
          type="button"
          className="person-multiselect__multi-toggle"
          onClick={() => {
            setAllowMultiple(true);
            setOpen(true);
            setTimeout(() => inputRef.current?.focus(), 0);
          }}
        >
          Mehrere Verantwortliche zulassen
        </button>
      ) : (
        <button
          type="button"
          className="person-multiselect__multi-toggle"
          onClick={() => {
            setAllowMultiple(false);
            if (selectedIds.length > 1) onChange(selectedIds.slice(0, 1));
          }}
        >
          Nur eine:n Verantwortliche:n
        </button>
      )}
    </div>
  );
};

// --- Einfache Checkliste innerhalb einer Pendenz --------------------------
const makeChecklistId = () =>
  `chk_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const Checklist = ({ items, onChange }) => {
  const [text, setText] = useState("");
  const list = items || [];

  const add = () => {
    const t = text.trim();
    if (!t) return;
    onChange([...list, { id: makeChecklistId(), text: t, done: false }]);
    setText("");
  };
  const toggle = (id) =>
    onChange(list.map((i) => (i.id === id ? { ...i, done: !i.done } : i)));
  const editText = (id, value) =>
    onChange(list.map((i) => (i.id === id ? { ...i, text: value } : i)));
  const remove = (id) => onChange(list.filter((i) => i.id !== id));

  return (
    <div className="checklist">
      {list.length > 0 && (
        <ul className="checklist__items">
          {list.map((i) => (
            <li
              key={i.id}
              className={`checklist__item${i.done ? " is-done" : ""}`}
            >
              <button
                type="button"
                className="checklist__toggle"
                onClick={() => toggle(i.id)}
                aria-label={i.done ? "Erledigt" : "Offen"}
                title={i.done ? "Als offen markieren" : "Als erledigt markieren"}
              >
                {i.done ? "●" : "○"}
              </button>
              <input
                type="text"
                className="checklist__text-input"
                value={i.text}
                onChange={(e) => editText(i.id, e.target.value)}
                placeholder="Punkt…"
              />
              <button
                type="button"
                className="checklist__remove"
                onClick={() => remove(i.id)}
                aria-label="Punkt entfernen"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="checklist__add">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Punkt hinzufügen…"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
        />
        <button type="button" className="pendenzen__btn" onClick={add}>
          Hinzufügen
        </button>
      </div>
    </div>
  );
};

// --- Gemeinsame Felder für Erfassungs- und Bearbeitungsformular -----------
export const PendenzFields = ({
  form,
  onChange,
  options,
  onCreateRequest,
  crmOptions,
  onLinkExisting,
  categoryOptions = [],
  showStatus = false,
  sitzungen = [],
  onCreateSitzungRequest,
  themenSuggestions = [],
  // true, wenn die Felder in einem <fieldset disabled> stehen: <fieldset>
  // deaktiviert contentEditable NICHT, darum muss der Rich-Text-Editor die
  // Sperre separat erhalten (siehe RichTextEditor.jsx).
  readOnly = false,
}) => {
  // Nach Auswahl einer (einzelnen) Verantwortlichkeit springt der Fokus hierher.
  const beschreibungRef = useRef(null);
  return (
  <div className="pendenzen__form-grid">
    <label className="pendenzen__field">
      <span>Art</span>
      <select value={form.typ} onChange={(e) => onChange("typ", e.target.value)}>
        {ART_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.value} – {o.label}
          </option>
        ))}
      </select>
    </label>

    <label className="pendenzen__field">
      <span>Kategorie</span>
      <select
        value={form.kategorie}
        onChange={(e) => onChange("kategorie", e.target.value)}
      >
        {categoryOptions.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
    </label>

    {showStatus && (
      <label className="pendenzen__field">
        <span>Status</span>
        <select
          value={form.status}
          onChange={(e) => onChange("status", e.target.value)}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
    )}

    <label className="pendenzen__field">
      <span>Deadline</span>
      <input
        type="date"
        value={form.terminSoll}
        onChange={(e) => onChange("terminSoll", e.target.value)}
      />
    </label>

    <label className="pendenzen__field">
      <span>neue Deadline</span>
      <input
        type="date"
        value={form.terminNeu}
        onChange={(e) => onChange("terminNeu", e.target.value)}
      />
    </label>

    <div className="pendenzen__field pendenzen__field--sitzung">
      <span>Sitzung</span>
      <div className="pendenzen__sitzung-row">
        <select
          value={form.sitzungId}
          onChange={(e) => onChange("sitzungId", e.target.value)}
        >
          <option value="">– keine –</option>
          {sitzungen.map((s) => (
            <option key={s.id} value={s.id}>
              {s.art} Nr. {s.nr} ({s.datum})
            </option>
          ))}
        </select>
        {onCreateSitzungRequest && (
          <button
            type="button"
            className="pendenzen__btn"
            onClick={onCreateSitzungRequest}
          >
            + Neu
          </button>
        )}
      </div>
    </div>

    <div className="pendenzen__field pendenzen__field--full">
      <span>Verantwortliche</span>
      <ResponsibleSelect
        options={options}
        selectedIds={form.verantwortlichIds}
        onChange={(ids) => onChange("verantwortlichIds", ids)}
        onCreateRequest={onCreateRequest}
        crmOptions={crmOptions}
        onLinkExisting={onLinkExisting}
        onCommit={() => beschreibungRef.current?.focus()}
      />
    </div>

    <div className="pendenzen__field pendenzen__field--full">
      <span>Themen</span>
      <ThemenSelect
        value={form.themen}
        onChange={(themen) => onChange("themen", themen)}
        suggestions={themenSuggestions}
      />
    </div>

    <div className="pendenzen__field pendenzen__field--full">
      <span>Beschreibung</span>
      <RichTextEditor
        ref={beschreibungRef}
        value={form.beschreibung}
        onChange={(html) => onChange("beschreibung", html)}
        editable={!readOnly}
        placeholder="Worum geht es?"
      />
    </div>

    <label className="pendenzen__field pendenzen__field--full">
      <span>Kommentar</span>
      <textarea
        value={form.kommentar}
        onChange={(e) => onChange("kommentar", e.target.value)}
        rows={2}
        placeholder="Optionaler Kommentar"
      />
    </label>

    <div className="pendenzen__field pendenzen__field--full">
      <span>Checkliste</span>
      <Checklist
        items={form.checkliste}
        onChange={(items) => onChange("checkliste", items)}
      />
    </div>

    <label className="pendenzen__field pendenzen__field--checkbox">
      <input
        type="checkbox"
        checked={form.sichtbarExtern}
        onChange={(e) => onChange("sichtbarExtern", e.target.checked)}
      />
      <span>Extern sichtbar</span>
    </label>
  </div>
  );
};

// --- Themen-Tags (frei, Mehrfach, Autocomplete aus bestehenden + neu anlegen) --
const ThemenSelect = ({ value, onChange, suggestions = [] }) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const tags = value || [];

  const add = (raw) => {
    const t = (raw || "").trim();
    if (!t) return;
    if (!tags.some((x) => x.toLowerCase() === t.toLowerCase())) {
      onChange([...tags, t]);
    }
    setQuery("");
  };
  const remove = (t) => onChange(tags.filter((x) => x !== t));

  const q = query.trim().toLowerCase();
  const matches = suggestions
    .filter((s) => !tags.some((x) => x.toLowerCase() === s.toLowerCase()))
    .filter((s) => (q ? s.toLowerCase().includes(q) : true))
    .slice(0, 20);
  const canCreate =
    !!q &&
    !suggestions.some((s) => s.toLowerCase() === q) &&
    !tags.some((x) => x.toLowerCase() === q);

  return (
    <div className="person-multiselect themen-select">
      {tags.length > 0 && (
        <div className="person-multiselect__chips">
          {tags.map((t) => (
            <span key={t} className="person-chip themen-chip">
              {t}
              <button type="button" onClick={() => remove(t)} aria-label={`${t} entfernen`}>
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="person-multiselect__input-wrap">
        <input
          type="text"
          value={query}
          placeholder="Thema suchen / hinzufügen…"
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add(query);
            }
          }}
        />
        {open && (matches.length > 0 || canCreate) && (
          <ul className="person-multiselect__dropdown">
            {matches.map((s) => (
              <li key={s}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    add(s);
                  }}
                >
                  <span>{s}</span>
                </button>
              </li>
            ))}
            {canCreate && (
              <li>
                <button
                  type="button"
                  className="person-multiselect__create"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    add(query);
                  }}
                >
                  ＋ „{query.trim()}" als neues Thema
                </button>
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

// --- Verortete Pendenz aufklappen: Planausschnitt mit Markierungen (US-PLAN-005) --
const PendenzPlanPanel = ({
  markups,
  plansWithVersions,
  project,
  activeGroupKey,
  onActiveGroupChange,
  focusMarkupId,
  onFocusMarkup,
  selectedMarkupId,
  onSelectMarkup,
}) => {
  const groups = useMemo(() => {
    const map = {};
    markups.forEach((m) => {
      const key = `${m.planVersionId}:${m.seite}`;
      (map[key] = map[key] || []).push(m);
    });
    return Object.entries(map).map(([key, items]) => {
      const [planVersionId, seiteStr] = key.split(":");
      const found = plansWithVersions.find((pv) =>
        pv.versions.some((v) => v.id === planVersionId)
      );
      return {
        key,
        planVersionId,
        seite: parseInt(seiteStr, 10),
        items,
        plan: found?.plan || null,
        versions: found?.versions || [],
      };
    });
  }, [markups, plansWithVersions]);

  const activeGroup = groups.find((g) => g.key === activeGroupKey) || groups[0];

  if (!activeGroup) {
    return (
      <div className="pendenzen__plan-panel">
        <p className="pendenzen__plan-empty">Keine Planzuordnung gefunden.</p>
      </div>
    );
  }

  return (
    <div className="pendenzen__plan-panel">
      {groups.length > 1 && (
        <div className="pendenzen__plan-list">
          {groups.map((g) => (
            <div
              key={g.key}
              className={`pendenzen__plan-group${
                g.key === activeGroup.key ? " is-active" : ""
              }`}
            >
              <button
                type="button"
                className="pendenzen__plan-group-title"
                onClick={() => {
                  onActiveGroupChange(g.key);
                  onFocusMarkup(g.items[0].id);
                  onSelectMarkup(g.items[0].id);
                }}
              >
                {g.plan?.name || "Plan"} · Seite {g.seite}
              </button>
              <ul>
                {g.items.map((m) => (
                  <li
                    key={m.id}
                    className={selectedMarkupId === m.id ? "is-selected" : ""}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        onActiveGroupChange(g.key);
                        onFocusMarkup(m.id);
                        onSelectMarkup(m.id);
                      }}
                    >
                      {m.beschriftung || `${m.typ}-Markierung`}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
      <div className="pendenzen__plan-viewer">
        {activeGroup.plan ? (
          <PlanViewer
            key={activeGroup.key}
            plan={activeGroup.plan}
            versions={activeGroup.versions}
            project={project}
            compact
            readOnly
            initialVersionId={activeGroup.planVersionId}
            initialSeite={activeGroup.seite}
            highlightMarkupIds={activeGroup.items.map((m) => m.id)}
            focusMarkupId={focusMarkupId}
            selectedMarkupId={selectedMarkupId}
            onMarkupSelect={onSelectMarkup}
          />
        ) : (
          <p className="pendenzen__plan-empty">
            Der zugehörige Plan wurde nicht gefunden (evtl. gelöscht).
          </p>
        )}
      </div>
    </div>
  );
};

// --- Plan auswählen für "Auf Plan verorten" (nur bei mehr als einem Plan) ---
const PlanPickerModal = ({ plansWithVersions, onSelect, onClose }) => (
  // Weder Klick auf den Hintergrund noch Escape schliessen (Nutzerwunsch, ADR
  // 0060): ein versehentlicher Klick ausserhalb oder ein Escape-Tastendruck
  // darf keine unfertige Eingabe verwerfen. Schliessen nur über das X oder
  // eine explizite Aktion.
  <div
    className="pendenzen__modal-overlay"
    onKeyDown={(e) => {
      if (e.key === "Escape") e.stopPropagation();
    }}
  >
    <div className="pendenzen__modal" onClick={(e) => e.stopPropagation()}>
      <div className="pendenzen__modal-header">
        <h3>Plan auswählen</h3>
        <button type="button" className="pendenzen__modal-close" onClick={onClose} aria-label="Schliessen">
          ×
        </button>
      </div>
      <div className="pendenzen__modal-body">
        <ul className="pendenzen__plan-picker-list">
          {plansWithVersions.map(({ plan, versions }) => (
            <li key={plan.id}>
              <button type="button" onClick={() => onSelect({ plan, versions })}>
                <span className="pendenzen__plan-picker-name">{plan.name}</span>
                {plan.geschoss && <span className="pendenzen__plan-picker-meta">{plan.geschoss}</span>}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

// --- "Neue Planpendenz": zuerst auf dem Plan markieren, danach die Pendenz-
// Angaben daneben erfassen. Speichert Pendenz und Markierung gemeinsam.
const NeuePlanpendenzModal = ({
  plansWithVersions,
  project,
  responsibleOptions,
  crmOptions,
  onCreateRequest,
  onLinkExisting,
  categories,
  sitzungen,
  onCreateSitzungRequest,
  themenSuggestions = [],
  onClose,
  onCreated,
}) => {
  const { user } = useAuth();
  const [selected, setSelected] = useState(
    plansWithVersions.length === 1 ? plansWithVersions[0] : null
  );
  const [form, setForm] = useState(emptyForm());
  const [beschriftung, setBeschriftung] = useState("");
  const [draft, setDraft] = useState(null); // { typ, geometrie, planVersionId, seite }
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const draftTypLabel = (typ) =>
    typ === "point" ? "Punkt" : typ === "line" ? "Linie" : "Fläche";

  const handleSave = async () => {
    if (isRichTextEmpty(form.beschreibung)) {
      setError("Bitte eine Beschreibung erfassen.");
      return;
    }
    if (!draft) {
      setError("Bitte zuerst eine Markierung auf dem Plan setzen.");
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const created = await createPendenz(project.id, {
        ...form,
        beschreibung: form.beschreibung,
        verantwortlich: verantwortlichDisplay(form.verantwortlichIds, responsibleOptions),
        verantwortlichExtern: verantwortlichDisplayExtern(form.verantwortlichIds, responsibleOptions),
        erstelltVon: user?.email || user?.userDetails || "",
      });
      await createMarkup(created.id, {
        projectUuid: project.id,
        planId: selected.plan.id,
        planVersionId: draft.planVersionId,
        seite: draft.seite,
        typ: draft.typ,
        geometrie: draft.geometrie,
        beschriftung,
        erstelltVon: user?.email || user?.userDetails || "",
      });
      onCreated(created);
      onClose();
    } catch (err) {
      setError(err.message || "Die Planpendenz konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="pendenzen__modal-overlay"
      onKeyDown={(e) => {
        if (e.key === "Escape") e.stopPropagation();
      }}
    >
      <div className="pendenzen__modal pendenzen__modal--wide" onClick={(e) => e.stopPropagation()}>
        <div className="pendenzen__modal-header">
          <h3>Neue Planpendenz</h3>
          <button type="button" className="pendenzen__modal-close" onClick={onClose} aria-label="Schliessen">
            ×
          </button>
        </div>

        {error && (
          <div className="pendenzen__error" role="alert" onClick={() => setError(null)}>
            {error}
          </div>
        )}

        {!selected ? (
          <div className="pendenzen__modal-body">
            <p>Auf welchem Plan soll die Pendenz platziert werden?</p>
            <ul className="pendenzen__plan-picker-list">
              {plansWithVersions.map((item) => (
                <li key={item.plan.id}>
                  <button type="button" onClick={() => setSelected(item)}>
                    <span className="pendenzen__plan-picker-name">{item.plan.name}</span>
                    {item.plan.geschoss && (
                      <span className="pendenzen__plan-picker-meta">{item.plan.geschoss}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="pendenzen__planpendenz-body">
            <div className="pendenzen__planpendenz-plan">
              <PlanViewer
                plan={selected.plan}
                versions={selected.versions}
                compact
                composeMode
                compactHeight="65vh"
                pendingDraft={draft}
                onDraftChange={setDraft}
              />
              {!draft ? (
                <p className="pendenzen__planpendenz-hint">
                  Werkzeug wählen (Punkt/Linie/Fläche) und auf den Plan klicken, um die Stelle zu
                  markieren.
                </p>
              ) : (
                <p className="pendenzen__planpendenz-hint pendenzen__planpendenz-hint--ok">
                  Markierung gesetzt ({draftTypLabel(draft.typ)}).{" "}
                  <button type="button" onClick={() => setDraft(null)}>
                    Entfernen &amp; neu setzen
                  </button>
                </p>
              )}
            </div>
            <div className="pendenzen__planpendenz-form">
              {/* Kein "+ Neu" für Sitzungen: Der Rückschreib-Handler des
                  Elternteils zielt auf dessen Inline-Formular, nicht auf dieses
                  Modal (Cross-Contamination). Hier nur Auswahl bestehender
                  Sitzungen; neue Sitzungen über das normale Erfassen anlegen. */}
              <PendenzFields
                form={form}
                onChange={handleChange}
                options={responsibleOptions}
                onCreateRequest={onCreateRequest}
                crmOptions={crmOptions}
                onLinkExisting={onLinkExisting}
                categoryOptions={categories}
                sitzungen={sitzungen}
                themenSuggestions={themenSuggestions}
              />
              <label className="pendenzen__field pendenzen__field--full">
                <span>Kurzbeschriftung der Markierung (optional)</span>
                <input
                  type="text"
                  value={beschriftung}
                  onChange={(e) => setBeschriftung(e.target.value)}
                  placeholder="z.B. Zufahrt Einstellhalle"
                />
              </label>
            </div>
          </div>
        )}

        <div className="pendenzen__modal-footer">
          <div className="pendenzen__modal-footer-right">
            <button type="button" className="pendenzen__btn" onClick={onClose} disabled={saving}>
              Abbrechen
            </button>
            {selected && (
              <button
                type="button"
                className="pendenzen__btn pendenzen__btn--primary"
                onClick={handleSave}
                disabled={saving || !draft}
              >
                {saving ? "Speichern..." : "Speichern"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Zuordnungs-/Neuerfassungs-Modal für eine im Plan gezeichnete Markierung --
// Wird geöffnet, wenn in der Plan-Ansicht mit einem Zeichenwerkzeug eine neue
// Markierung gesetzt wurde. Anders als das Mini-Popup im PlanViewer bietet der
// Reiter "Neue Pendenz" hier das VOLLE Pendenz-Formular (Kategorie, Verant-
// wortliche, Termine, Kommentar, Checkliste, Sitzung, extern sichtbar). Lebt
// bewusst im Elternteil, wo alle Stammdaten/Handler (CRM, Kategorien, Sitzungen)
// bereits vorliegen – so entfällt Prop-Drilling durch den PlanViewer und ein
// zirkulärer Import von PendenzFields.
const PlanAssignModal = ({
  draft,
  pendenzen,
  project,
  responsibleOptions,
  crmOptions,
  onCreateRequest,
  onLinkExisting,
  categories,
  sitzungen,
  themenSuggestions = [],
  onClose,
  onSaved,
}) => {
  const { user } = useAuth();
  // Default "new": beim Zeichnen in der Plan-Ansicht wird meist ein neues Thema
  // erfasst (gleiche Vorbelegung wie zuvor defaultAssignMode="new").
  const [mode, setMode] = useState("new");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [form, setForm] = useState(emptyForm());
  const [beschriftung, setBeschriftung] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const draftTypLabel =
    draft.typ === "point" ? "Punkt" : draft.typ === "line" ? "Linie" : "Fläche";

  const filtered = pendenzen.filter((p) =>
    search.trim()
      ? richTextToPlainText(p.beschreibung).toLowerCase().includes(search.trim().toLowerCase())
      : true
  );

  const handleSave = async () => {
    try {
      let targetId = selectedId;
      if (mode === "new") {
        if (isRichTextEmpty(form.beschreibung)) {
          setError("Bitte eine Beschreibung erfassen.");
          return;
        }
      } else if (!selectedId) {
        setError("Bitte eine Pendenz auswählen.");
        return;
      }
      setSaving(true);
      setError(null);
      if (mode === "new") {
        const created = await createPendenz(project.id, {
          ...form,
          beschreibung: form.beschreibung,
          verantwortlich: verantwortlichDisplay(form.verantwortlichIds, responsibleOptions),
          verantwortlichExtern: verantwortlichDisplayExtern(form.verantwortlichIds, responsibleOptions),
          erstelltVon: user?.email || user?.userDetails || "",
        });
        targetId = created.id;
      }
      const markup = await createMarkup(targetId, {
        projectUuid: project.id,
        planId: draft.planId,
        planVersionId: draft.planVersionId,
        seite: draft.seite,
        typ: draft.typ,
        geometrie: draft.geometrie,
        beschriftung,
        erstelltVon: user?.email || user?.userDetails || "",
      });
      onSaved(markup);
    } catch (err) {
      setError(err.message || "Die Markierung konnte nicht gespeichert werden.");
      setSaving(false);
    }
  };

  return (
    <div
      className="pendenzen__modal-overlay"
      onKeyDown={(e) => {
        if (e.key === "Escape") e.stopPropagation();
      }}
    >
      <div
        className="pendenzen__modal pendenzen__modal--wide"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pendenzen__modal-header">
          <h3>Markierung zuordnen ({draftTypLabel})</h3>
          <button
            type="button"
            className="pendenzen__modal-close"
            onClick={onClose}
            aria-label="Schliessen"
          >
            ×
          </button>
        </div>

        {error && (
          <div className="pendenzen__error" role="alert" onClick={() => setError(null)}>
            {error}
          </div>
        )}

        <div className="pendenzen__modal-body">
          <div className="pendenzen__assign-tabs">
            <button
              type="button"
              className={`pendenzen__btn${mode === "existing" ? " pendenzen__btn--primary" : ""}`}
              onClick={() => setMode("existing")}
            >
              Bestehende Pendenz
            </button>
            <button
              type="button"
              className={`pendenzen__btn${mode === "new" ? " pendenzen__btn--primary" : ""}`}
              onClick={() => setMode("new")}
            >
              Neue Pendenz
            </button>
          </div>

          {mode === "existing" ? (
            <div className="pendenzen__form-grid">
              <label className="pendenzen__field pendenzen__field--full">
                <span>Pendenz suchen</span>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Suchen..."
                />
              </label>
              <label className="pendenzen__field pendenzen__field--full">
                <span>Pendenz</span>
                <select
                  size={6}
                  value={selectedId}
                  onChange={(e) => setSelectedId(e.target.value)}
                >
                  {filtered.map((p) => (
                    <option key={p.id} value={p.id}>
                      Nr. {p.nummer} – {richTextToPlainText(p.beschreibung)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : (
            <PendenzFields
              form={form}
              onChange={handleChange}
              options={responsibleOptions}
              onCreateRequest={onCreateRequest}
              crmOptions={crmOptions}
              onLinkExisting={onLinkExisting}
              categoryOptions={categories}
              sitzungen={sitzungen}
              themenSuggestions={themenSuggestions}
            />
          )}

          <label className="pendenzen__field pendenzen__field--full">
            <span>Kurzbeschriftung der Markierung (optional)</span>
            <input
              type="text"
              value={beschriftung}
              onChange={(e) => setBeschriftung(e.target.value)}
              placeholder="z.B. Zufahrt Einstellhalle"
            />
          </label>
        </div>

        <div className="pendenzen__modal-footer">
          <div className="pendenzen__modal-footer-right">
            <button type="button" className="pendenzen__btn" onClick={onClose} disabled={saving}>
              Abbrechen
            </button>
            <button
              type="button"
              className="pendenzen__btn pendenzen__btn--primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Speichern..." : "Speichern"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Ansicht "Plan": ein Plan gross, daneben nur die betroffenen Pendenzen --
// Wechselt bei mehreren Plänen zwischen ihnen; die Pendenzenliste wechselt
// dabei mit (immer nur die Pendenzen, die auf dem gewählten Plan verortet
// sind, siehe Nutzeranforderung "immer nur die betroffenen Referenzen").
const PendenzenPlanView = ({
  plansWithVersions,
  selectedPlanId,
  onSelectPlan,
  pendenzen,
  markupsByPendenz,
  project,
  readOnly,
  seite,
  focusMarkupId,
  // Springt zu einer bestimmten Pendenz auf dem aktuell gewählten Plan.
  // Bewusst eine Elternfunktion (statt hier lokal aufgelöst): der Zielort kann
  // auf einer ANDEREN Seite des Plans liegen als aktuell angezeigt, und nur
  // der Elternteil kennt/setzt die Seite (planViewSeite), die PlanViewers
  // Remount-Key steuert. Ein rein lokales onFocusMarkup/onSelectMarkup ohne
  // Seitenwechsel würde bei einer Pendenz auf einer anderen Seite lautlos
  // nichts bewirken.
  onFocusPendenz,
  selectedMarkupId,
  onSelectMarkup,
  onDataChanged,
  // Öffnet eine Pendenz direkt im Bearbeiten-Modal (Klick auf Markierung im
  // Plan oder auf einen Listeneintrag).
  onOpenPendenz,
  // Meldet eine frisch gezeichnete Markierung an den Elternteil, der daraufhin
  // das volle Zuordnungs-/Neuerfassungs-Modal öffnet.
  onRequestAssign,
  // Erzwingt ein Neuladen der Markierungen im Viewer nach externen Änderungen.
  reloadToken,
}) => {
  const selected = plansWithVersions.find(({ plan }) => plan.id === selectedPlanId) || null;
  const selectedPlanIdResolved = selected?.plan.id;

  // Stabil halten, damit `finishDraw` im PlanViewer nicht bei jedem Pan-Render
  // neu erzeugt wird (sonst würde der keydown-Listener dort ständig getauscht).
  const handleRequestAssign = useCallback(
    (draft) =>
      selectedPlanIdResolved &&
      onRequestAssign?.({ ...draft, planId: selectedPlanIdResolved }),
    [onRequestAssign, selectedPlanIdResolved]
  );

  // Aufgeklappter Listeneintrag (zusätzliche Infos direkt neben dem Plan).
  const [expandedPendenzId, setExpandedPendenzId] = useState(null);
  // Beim Plan-Wechsel Aufklappzustand zurücksetzen (konsistent zur im Elternteil
  // zurückgesetzten Auswahl in switchPlanView).
  useEffect(() => {
    setExpandedPendenzId(null);
  }, [selectedPlanId]);

  const relevantPendenzen = selected
    ? pendenzen.filter((p) =>
        (markupsByPendenz[p.id] || []).some((m) => m.planId === selected.plan.id)
      )
    : [];

  if (plansWithVersions.length === 0) {
    return (
      <div className="pendenzen__planview">
        <p className="pendenzen__plan-empty">
          Noch keine Pläne vorhanden. Bitte zuerst im Tab "Pläne" einen Plan hochladen.
        </p>
      </div>
    );
  }

  return (
    <div className="pendenzen__planview">
      {plansWithVersions.length > 1 && (
        <div className="pendenzen__planview-switcher">
          {plansWithVersions.map(({ plan }) => (
            <button
              key={plan.id}
              type="button"
              className={`pendenzen__planview-tab${
                plan.id === selectedPlanId ? " is-active" : ""
              }`}
              onClick={() => onSelectPlan(plan.id)}
            >
              {plan.name}
            </button>
          ))}
        </div>
      )}

      {!selected ? (
        <p className="pendenzen__plan-empty">Plan wird geladen…</p>
      ) : (
        <div className="pendenzen__planview-body">
          <div className="pendenzen__planview-viewer">
            <PlanViewer
              key={`${selected.plan.id}:${seite}`}
              plan={selected.plan}
              versions={selected.versions}
              project={project}
              readOnly={readOnly}
              compact
              compactHeight="75vh"
              initialSeite={seite}
              highlightMarkupIds={relevantPendenzen.flatMap((p) =>
                (markupsByPendenz[p.id] || [])
                  .filter((m) => m.planId === selected.plan.id)
                  .map((m) => m.id)
              )}
              // Suche/Filter blenden auch die Markierungen im Plan aus: nur die
              // aktuell in der Liste sichtbaren (gefilterten) Pendenzen zeichnen.
              visiblePendenzIds={relevantPendenzen.map((p) => p.id)}
              focusMarkupId={focusMarkupId}
              selectedMarkupId={selectedMarkupId}
              onMarkupSelect={onSelectMarkup}
              onDataChanged={onDataChanged}
              onOpenPendenz={onOpenPendenz}
              defaultAssignMode="new"
              onRequestAssign={handleRequestAssign}
              reloadToken={reloadToken}
            />
          </div>
          <div className="pendenzen__planview-list">
            <h4>Pendenzen auf diesem Plan</h4>
            {relevantPendenzen.length === 0 ? (
              <p className="pendenzen__plan-empty">
                Keine Pendenzen auf diesem Plan verortet.
              </p>
            ) : (
              <ul>
                {relevantPendenzen.map((p) => {
                  const markup = (markupsByPendenz[p.id] || []).find(
                    (m) => m.planId === selected.plan.id
                  );
                  const isSelected = !!markup && selectedMarkupId === markup.id;
                  const isExpanded = expandedPendenzId === p.id;
                  const termin = massgeblicherTermin(p);
                  const statusLabel =
                    STATUS_OPTIONS.find((o) => o.value === p.status)?.label ||
                    p.status;
                  return (
                    <li
                      key={p.id}
                      className={`pendenzen__planview-item${
                        isSelected ? " is-selected" : ""
                      }`}
                    >
                      <div className="pendenzen__planview-item-row">
                        <button
                          type="button"
                          className="pendenzen__planview-item-main"
                          onClick={() => {
                            // Klick zentriert die Markierung im Plan und klappt
                            // die Pendenz für zusätzliche Infos auf/zu.
                            onFocusPendenz(p);
                            setExpandedPendenzId((prev) =>
                              prev === p.id ? null : p.id
                            );
                          }}
                        >
                          <span className={`art-badge art-badge--${p.typ}`}>{p.typ}</span>
                          <span className="pendenzen__planview-nr">Nr. {p.nummer}</span>
                          <span className="pendenzen__planview-desc">
                            {richTextToPlainText(p.beschreibung)}
                          </span>
                        </button>
                        <button
                          type="button"
                          className="pendenzen__planview-edit"
                          onClick={() => onOpenPendenz(p.id)}
                        >
                          Bearbeiten
                        </button>
                      </div>
                      {isExpanded && (
                        <div className="pendenzen__planview-detail">
                          {p.verantwortlich && (
                            <div>
                              <strong>Verantwortlich:</strong> {p.verantwortlich}
                            </div>
                          )}
                          {termin && (
                            <div>
                              <strong>Deadline:</strong> {formatDate(termin)}
                            </div>
                          )}
                          <div>
                            <strong>Status:</strong> {statusLabel}
                          </div>
                          {p.kommentar && (
                            <div>
                              <strong>Kommentar:</strong> {p.kommentar}
                            </div>
                          )}
                          {(p.checkliste || []).length > 0 && (
                            <ul className="pendenzen__planview-checklist">
                              {p.checkliste.map((i) => (
                                <li key={i.id} className={i.done ? "is-done" : ""}>
                                  <span className="pendenzen__planview-check-icon">
                                    {i.done ? "●" : "○"}
                                  </span>
                                  {i.text}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// --- Sammelbearbeitung mehrerer Pendenzen (Multiedit) ------------------------
// Zeigt die üblichen Felder mit je einem "ändern"-Schalter. Nur angetippte Felder
// werden auf ALLE markierten Pendenzen angewendet (überschreiben) – so gehen
// nicht angefasste Felder nicht verloren.
const BulkEditModal = ({
  count,
  responsibleOptions,
  crmOptions,
  onCreateRequest,
  onLinkExisting,
  categories,
  sitzungen,
  themenSuggestions,
  saving,
  onClose,
  onApply,
}) => {
  const [apply, setApply] = useState({});
  const [vals, setVals] = useState({
    typ: "P",
    kategorie: categories[0]?.id || "Org",
    themen: [],
    verantwortlichIds: [],
    terminSoll: "",
    terminNeu: "",
    status: "offen",
    sitzungId: "",
    kommentar: "",
    sichtbarExtern: true,
  });
  // Eingabe in ein Feld aktiviert es automatisch (kein separater Schalter nötig).
  const setVal = (k, v) => {
    setVals((prev) => ({ ...prev, [k]: v }));
    setApply((prev) => (prev[k] ? prev : { ...prev, [k]: true }));
  };
  const toggle = (k) => setApply((prev) => ({ ...prev, [k]: !prev[k] }));

  const handleSubmit = () => {
    const changes = {};
    if (apply.typ) changes.typ = vals.typ;
    if (apply.kategorie) changes.kategorie = vals.kategorie;
    if (apply.themen) changes.themen = vals.themen;
    if (apply.verantwortlich) changes.verantwortlichIds = vals.verantwortlichIds;
    if (apply.terminSoll) changes.terminSoll = vals.terminSoll;
    if (apply.terminNeu) changes.terminNeu = vals.terminNeu;
    if (apply.status) changes.status = vals.status;
    if (apply.sitzungId) changes.sitzungId = vals.sitzungId;
    if (apply.kommentar) changes.kommentar = vals.kommentar;
    if (apply.sichtbarExtern) changes.sichtbarExtern = vals.sichtbarExtern;
    onApply(changes);
  };

  const anyChecked = Object.values(apply).some(Boolean);

  // Render-Hilfsfunktion (bewusst KEINE eigene Komponente + <Row/>, sonst würde
  // sie bei jedem Tastendruck neu gemountet → Fokusverlust in den Feldern).
  // Der Haken zeigt an, ob das Feld angewendet wird; er setzt sich beim Reinschreiben
  // automatisch, kann aber manuell wieder entfernt werden (Feld ausschliessen).
  const row = (field, label, control) => (
    <div className={`pendenzen__bulk-row${apply[field] ? " is-active" : ""}`}>
      <label className="pendenzen__bulk-toggle">
        <input type="checkbox" checked={!!apply[field]} onChange={() => toggle(field)} />
        <span>{label}</span>
      </label>
      <div className="pendenzen__bulk-control">{control}</div>
    </div>
  );

  return (
    <div
      className="pendenzen__modal-overlay"
      onKeyDown={(e) => {
        if (e.key === "Escape") e.stopPropagation();
      }}
    >
      <div
        className="pendenzen__modal pendenzen__modal--wide"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pendenzen__modal-header">
          <h3>{count} Pendenzen bearbeiten</h3>
          <button
            type="button"
            className="pendenzen__modal-close"
            onClick={onClose}
            aria-label="Schliessen"
          >
            ×
          </button>
        </div>

        <div className="pendenzen__modal-body">
          <p className="pendenzen__hint">
            Sobald du ein Feld ausfüllst, wird es angewendet – bei allen {count}{" "}
            ausgewählten Pendenzen (bestehende Werte werden überschrieben). Über den
            Haken kannst du ein Feld wieder ausschliessen.
          </p>

          {row(
            "typ",
            "Art",
            <select
              value={vals.typ}
              onChange={(e) => setVal("typ", e.target.value)}
            >
              {ART_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.value} – {o.label}
                </option>
              ))}
            </select>
          )}

          {row(
            "kategorie",
            "Kategorie",
            <select
              value={vals.kategorie}
              onChange={(e) => setVal("kategorie", e.target.value)}
            >
              {categories.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          )}

          {row(
            "themen",
            "Themen (ersetzt)",
            <ThemenSelect
              value={vals.themen}
              onChange={(themen) => setVal("themen", themen)}
              suggestions={themenSuggestions}
            />
          )}

          {row(
            "verantwortlich",
            "Verantwortliche (ersetzt)",
            <ResponsibleSelect
              options={responsibleOptions}
              selectedIds={vals.verantwortlichIds}
              onChange={(ids) => setVal("verantwortlichIds", ids)}
              onCreateRequest={onCreateRequest}
              crmOptions={crmOptions}
              onLinkExisting={onLinkExisting}
            />
          )}

          {row(
            "terminSoll",
            "Deadline",
            <input
              type="date"
              value={vals.terminSoll}
              onChange={(e) => setVal("terminSoll", e.target.value)}
            />
          )}

          {row(
            "terminNeu",
            "neue Deadline",
            <input
              type="date"
              value={vals.terminNeu}
              onChange={(e) => setVal("terminNeu", e.target.value)}
            />
          )}

          {row(
            "status",
            "Status",
            <select
              value={vals.status}
              onChange={(e) => setVal("status", e.target.value)}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          )}

          {row(
            "sitzungId",
            "Sitzung",
            <select
              value={vals.sitzungId}
              onChange={(e) => setVal("sitzungId", e.target.value)}
            >
              <option value="">– keine –</option>
              {sitzungen.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.art} Nr. {s.nr} ({s.datum})
                </option>
              ))}
            </select>
          )}

          {row(
            "kommentar",
            "Kommentar (ersetzt)",
            <textarea
              rows={2}
              value={vals.kommentar}
              onChange={(e) => setVal("kommentar", e.target.value)}
            />
          )}

          {row(
            "sichtbarExtern",
            "Extern sichtbar",
            <select
              value={vals.sichtbarExtern ? "1" : "0"}
              onChange={(e) => setVal("sichtbarExtern", e.target.value === "1")}
            >
              <option value="1">Ja</option>
              <option value="0">Nein</option>
            </select>
          )}
        </div>

        <div className="pendenzen__modal-footer">
          <div className="pendenzen__modal-footer-right">
            <button type="button" className="pendenzen__btn" onClick={onClose} disabled={saving}>
              Abbrechen
            </button>
            <button
              type="button"
              className="pendenzen__btn pendenzen__btn--primary"
              onClick={handleSubmit}
              disabled={saving || !anyChecked}
            >
              {saving ? "Speichern..." : `Auf ${count} anwenden`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Pendenzen = ({
  projectUuid,
  projectCode,
  onBack,
  projectTitle,
  listKind = "project", // "project" | "team" | "admin"
}) => {
  const { user } = useAuth();
  const location = useLocation();
  // Wird aus "Meine Pendenzen" mit { state: { createNew } } aufgerufen: dann direkt
  // das Erfassungsformular öffnen (einmalig, sobald Bearbeitungsrecht feststeht).
  const createNewConsumed = useRef(false);
  const [pendenzen, setPendenzen] = useState([]);
  // Rohdaten für den Aufbau der Verantwortlich-Optionen
  const [partners, setPartners] = useState([]);
  const [persons, setPersons] = useState([]);
  const [relations, setRelations] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  // Sitzungsgefässe (US-SITZ-001)
  const [sitzungen, setSitzungen] = useState([]);
  const [sitzungModal, setSitzungModal] = useState(null); // { target: "form" | "edit" } oder null

  // Verortete Pendenzen: Markierungen + Pläne (US-PLAN-005)
  const [allMarkups, setAllMarkups] = useState([]);
  const [plansWithVersions, setPlansWithVersions] = useState([]);
  const [expandedPlanPendenzId, setExpandedPlanPendenzId] = useState(null);
  const [activeGroupKey, setActiveGroupKey] = useState(null); // `${planVersionId}:${seite}`
  const [focusMarkupId, setFocusMarkupId] = useState(null);
  const [selectedMarkupId, setSelectedMarkupId] = useState(null);
  // "Auf Plan verorten" direkt aus der Liste: Plan-Auswahl (bei >1 Plan) bzw.
  // der eigentliche Viewer mit fest zugeordneter Pendenz.
  const [pickerPendenz, setPickerPendenz] = useState(null);
  const [verortungTarget, setVerortungTarget] = useState(null); // { pendenz, plan, versions }

  // Board-Card "Plan" bei einer Pendenz mit Markierungen auf mehreren Plänen:
  // zeigt zunächst eine Planauswahl (wie openPlanPicker), statt stillschweigend
  // die älteste Markierung zu wählen.
  const [boardPlanPickerPendenz, setBoardPlanPickerPendenz] = useState(null);

  // Ansicht "Plan": ein Plan gross, daneben nur die betroffenen Pendenzen.
  const [planViewPlanId, setPlanViewPlanId] = useState(null);
  const [planViewSeite, setPlanViewSeite] = useState(1);
  const [planViewFocusMarkupId, setPlanViewFocusMarkupId] = useState(null);
  const [planViewSelectedMarkupId, setPlanViewSelectedMarkupId] = useState(null);
  // In der Plan-Ansicht frisch gezeichnete, noch zuzuordnende Markierung
  // (öffnet das volle Zuordnungs-/Neuerfassungs-Modal). null = keins offen.
  const [planAssignDraft, setPlanAssignDraft] = useState(null);
  // Zähler, um den eingebetteten Plan-Viewer nach externen Markierungs-Anlagen
  // (über das Zuordnungs-Modal) zum Neuladen anzustossen.
  const [planViewReloadToken, setPlanViewReloadToken] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  // CRM-Modal "Neue Verantwortliche anlegen": { type, name, addFn }
  const [crmModal, setCrmModal] = useState(null);
  // Zuordnungs-Modal für bestehende CRM-Einträge: { option, addFn }
  const [linkModal, setLinkModal] = useState(null);

  const responsibleOptions = useMemo(
    () => buildResponsibleOptions(partners, persons, relations, teamMembers),
    [partners, persons, relations, teamMembers]
  );

  // Im CRM vorhandene Organisationen/Personen, die (noch) nicht dem Projekt
  // zugeordnet sind – zum nachträglichen Verbinden aus der Auswahl heraus.
  const crmOptions = useMemo(() => {
    const assigned = new Set(responsibleOptions.map((o) => o.id));
    const orgs = partners.map((p) => ({
      id: `org:${p.id}`,
      label: p.name,
      group: "Organisation",
    }));
    const pers = persons.map((p) => ({
      id: `person:${p.id}`,
      label:
        `${p.firstName || ""} ${p.lastName || ""}`.trim() ||
        p.email ||
        "(ohne Name)",
      group: "Person",
    }));
    return [...orgs, ...pers].filter((o) => !assigned.has(o.id));
  }, [partners, persons, responsibleOptions]);

  // Erfassungsformular (inline)
  const [showForm, setShowForm] = useState(false);
  const [showNeuePlanpendenz, setShowNeuePlanpendenz] = useState(false);
  const [showPlaeneModal, setShowPlaeneModal] = useState(false);
  const [form, setForm] = useState(emptyForm());

  // Bearbeitungs-Modal (US-PEND-002)
  const [editing, setEditing] = useState(null); // Original-Pendenz oder null
  const [editForm, setEditForm] = useState(null);

  // Teilen-Dialog (US-PEND-004)
  const [showShare, setShowShare] = useState(false);
  const [shares, setShares] = useState([]);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [shareError, setShareError] = useState(null);
  const [newExpiresAt, setNewExpiresAt] = useState("");
  const [copiedToken, setCopiedToken] = useState(null);

  // Projektspezifische Kategorien (fix + custom) und Ansicht (Liste/Board)
  const [customCategories, setCustomCategories] = useState([]);
  const [view, setView] = useState("list");
  // Board-Gruppierung: nach Kategorie (mit DnD), Thema oder Zuständigkeit
  // (jeweils "das erste zählt", reine Gruppierungsansicht ohne DnD).
  const [boardGroupBy, setBoardGroupBy] = useState("kategorie");
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Multiselect/Multiedit (Listenansicht): per "Multi"-Button ein-/ausschaltbar.
  const [multiMode, setMultiMode] = useState(false);
  const [bulkSelected, setBulkSelected] = useState(() => new Set());
  const [showBulkEdit, setShowBulkEdit] = useState(false);

  // Sitzungsmodus: fokussiertes Erfassen während einer Sitzung.
  const [sitzungModusActive, setSitzungModusActive] = useState(false);
  const [sitzungModusId, setSitzungModusId] = useState(null); // laufende Sitzung
  const [sitzungModusShowPast, setSitzungModusShowPast] = useState(false);
  const [showSitzungSetup, setShowSitzungSetup] = useState(false);
  const [sitzungModusSaving, setSitzungModusSaving] = useState(false);
  const [sitzungForm, setSitzungForm] = useState(null); // {art,nr,datum,vonZeit,bisZeit,anwesende}
  // Notizen (Art N) sind in der allgemeinen Liste standardmässig ausgeblendet.
  const [showNotizen, setShowNotizen] = useState(false);
  // Auswahl vor dem eigentlichen Start: neue Sitzung anlegen ODER eine
  // bestehende (auch vergangene) Sitzung zum Weiterbearbeiten öffnen.
  const [sitzungModusChooserOpen, setSitzungModusChooserOpen] = useState(false);
  // Protokoll (Excel/PDF, US-SITZ-002/005) für die laufende Sitzung — dasselbe
  // Modal wie im Sitzungen-Tab, hier direkt aus dem Sitzungsmodus erreichbar.
  const [showProtokollModal, setShowProtokollModal] = useState(false);
  const [sitzungResumeSelection, setSitzungResumeSelection] = useState("");

  // Inline-Schnellerfassung: leere Zeile zuunterst in der Liste, direkt editierbar.
  // kategorie=null bedeutet "vom Eintrag direkt darüber übernehmen"; Datum-Default
  // ist immer heute.
  const [quickAdd, setQuickAdd] = useState({
    typ: "P",
    kategorie: null,
    beschreibung: "",
    verantwortlichIds: [],
    terminSoll: todayIso(),
    status: "offen",
  });
  const [quickSaving, setQuickSaving] = useState(false);
  const quickDeadlineRef = useRef(null);
  // RichTextEditor liest `content` nur beim Mounten (Tiptap-Verhalten) – nach
  // dem Commit erzwingt ein geänderter key einen Remount, damit die Zeile
  // wieder leer ist (siehe RichTextEditor.jsx-Kommentar).
  const [quickAddEditorKey, setQuickAddEditorKey] = useState(0);

  // Projekt-Mitglieder (Bearbeitungsrechte) – wie im Controlling
  const [assignments, setAssignments] = useState([]);
  const [showMembers, setShowMembers] = useState(false);

  // Bearbeitungsrecht: Team-Liste = alle; Admin-Liste = nur GL; Projekt = GL/PL immer,
  // sonst nur wenn dem Projekt zugeordnet (projectEmployeeAssignments).
  const isAdmin = hasRole(user, ROLES.ADMIN);
  const canEdit = useMemo(() => {
    if (listKind === "team") return true;
    if (listKind === "admin") return isAdmin;
    // Projektliste: gemeinsame Regel mit Controlling/Kostenschätzung/Termine.
    return canEditProjectAssigned(user, assignments);
  }, [listKind, isAdmin, user, assignments]);
  const readOnly = !canEdit;

  useEffect(() => {
    if (!isFullscreen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setIsFullscreen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isFullscreen]);

  const categories = useMemo(
    () => [
      ...FIXED_CATEGORIES.map((c) => ({ id: c.id, label: c.label, fixed: true })),
      ...customCategories.map((c) => ({ id: c.id, label: c.label, fixed: false })),
    ],
    [customCategories]
  );
  const kategorieLabelFor = useCallback(
    (id) => categories.find((c) => c.id === id)?.label || "",
    [categories]
  );

  // Autocomplete-Quelle für Themen: alle bereits im Projekt vergebenen Themen.
  const allThemen = useMemo(() => {
    const set = new Set();
    pendenzen.forEach((p) => (p.themen || []).forEach((t) => t && set.add(t)));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [pendenzen]);

  // Markierungen je Pendenz gruppiert (US-PLAN-005: Zähler/Icon + Aufklapp-Panel).
  const markupsByPendenz = useMemo(() => {
    const map = {};
    allMarkups.forEach((m) => {
      (map[m.pendenzId] = map[m.pendenzId] || []).push(m);
    });
    return map;
  }, [allMarkups]);

  const toggleExpandPlan = (pendenzId) => {
    if (expandedPlanPendenzId === pendenzId) {
      setExpandedPlanPendenzId(null);
      setActiveGroupKey(null);
      setFocusMarkupId(null);
      setSelectedMarkupId(null);
      return;
    }
    setExpandedPlanPendenzId(pendenzId);
    setSelectedMarkupId(null);
    const markups = markupsByPendenz[pendenzId] || [];
    const first = markups[0];
    setActiveGroupKey(first ? `${first.planVersionId}:${first.seite}` : null);
    setFocusMarkupId(first ? first.id : null);
  };

  // "+ Plan" in der Zeile: bei genau einem Plan direkt den Viewer öffnen,
  // bei mehreren zunächst den Plan auswählen lassen, bei keinem einen Hinweis zeigen.
  const openPlanPicker = (pendenz) => {
    if (plansWithVersions.length === 0) {
      setError(
        'Es sind noch keine Pläne vorhanden. Bitte zuerst im Tab "Pläne" einen Plan hochladen.'
      );
      return;
    }
    if (plansWithVersions.length === 1) {
      const { plan, versions } = plansWithVersions[0];
      setVerortungTarget({ pendenz, plan, versions });
      return;
    }
    setPickerPendenz(pendenz);
  };

  // Nach dem Verorten (oder Schliessen ohne Änderung) die Markierungen neu laden,
  // damit Zähler/Icon und das Aufklapp-Panel aktuell sind.
  const closeVerortung = async () => {
    setVerortungTarget(null);
    try {
      setAllMarkups(await getMarkupsForProject(projectUuid));
    } catch {
      /* ignore, nächster voller Reload holt es nach */
    }
  };

  // Pläne-Popup schliessen: Planliste neu laden, damit neu hochgeladene/
  // gelöschte Pläne sofort in "+ Plan"/"+ Neue Planpendenz" verfügbar sind.
  const closePlaeneModal = async () => {
    setShowPlaeneModal(false);
    try {
      setPlansWithVersions(await getPlansWithVersions(projectUuid));
    } catch {
      /* ignore, nächster voller Reload holt es nach */
    }
  };

  // Ansicht "Plan" auf einen bestimmten Plan umschalten. Beim Laden eines Plans
  // ist bewusst KEINE Pendenz vorausgewählt (Nutzerwunsch) – der Plan startet auf
  // Seite 1 ohne Auswahl/Fokus. Ein gezieltes Anspringen einer Pendenz erfolgt
  // separat (Board-Karte "Plan", Klick in der Liste) über jumpToPendenzOnPlan /
  // focusPendenzInPlanView.
  const switchPlanView = (planId) => {
    setPlanViewPlanId(planId);
    setPlanViewSeite(1);
    setPlanViewFocusMarkupId(null);
    setPlanViewSelectedMarkupId(null);
  };

  // Springt innerhalb der Plan-Ansicht zu einer bestimmten Pendenz auf dem
  // AKTUELL gewählten Plan – auch wenn ihre Markierung auf einer anderen Seite
  // liegt als gerade angezeigt (daher wird planViewSeite mitgesetzt, nicht nur
  // Fokus/Auswahl; sonst bliebe der Sprung bei mehrseitigen Plänen lautlos wirkungslos).
  const focusPendenzInPlanView = (pendenz) => {
    const markup = (markupsByPendenz[pendenz.id] || []).find(
      (m) => m.planId === planViewPlanId
    );
    if (!markup) return;
    setPlanViewSeite(markup.seite || 1);
    setPlanViewFocusMarkupId(markup.id);
    setPlanViewSelectedMarkupId(markup.id);
  };

  // Springt zu einer Pendenz auf einem BESTIMMTEN Plan (auch wenn das nicht
  // der aktuell gewählte ist) und wechselt in die Plan-Ansicht.
  const jumpToPendenzOnPlan = (pendenz, planId) => {
    const markup = (markupsByPendenz[pendenz.id] || []).find((m) => m.planId === planId);
    if (!markup) return;
    setPlanViewPlanId(planId);
    setPlanViewSeite(markup.seite || 1);
    setPlanViewFocusMarkupId(markup.id);
    setPlanViewSelectedMarkupId(markup.id);
    setView("plan");
  };

  // Board-Card "Plan": öffnet die Plan-Ansicht bei der Markierung dieser
  // Pendenz. Hat die Pendenz Markierungen auf MEHREREN Plänen, wird zuerst
  // ausgewählt, welcher gemeint ist (wie beim "+ Plan"-Direkteinstieg über
  // openPlanPicker) statt stillschweigend die älteste Markierung zu zeigen.
  const openPlanViewForPendenz = (pendenz) => {
    const markups = markupsByPendenz[pendenz.id] || [];
    if (markups.length === 0) return;
    const distinctPlanIds = Array.from(new Set(markups.map((m) => m.planId)));
    if (distinctPlanIds.length === 1) {
      jumpToPendenzOnPlan(pendenz, distinctPlanIds[0]);
      return;
    }
    setBoardPlanPickerPendenz(pendenz);
  };

  // Lightweight-Refresh für Änderungen direkt im eingebetteten PlanViewer der
  // Plan-Ansicht (Markierung anlegen/löschen/verschieben, Position bestätigen,
  // ggf. neue Pendenz). Bewusst OHNE loadData()/setLoading: loadData setzt den
  // globalen "loading"-Flag, der die gesamte Pendenzenliste (inkl. des gerade
  // benutzten Viewers samt Zoom/Pan/Werkzeug) durch eine Ladeanzeige ersetzt –
  // für eine einzelne Markierungsänderung wäre das ein störender Vollreload.
  const refreshAfterPlanViewChange = async () => {
    try {
      const [pendData, markupsData] = await Promise.all([
        getPendenzen(projectUuid),
        getMarkupsForProject(projectUuid),
      ]);
      setPendenzen(pendData);
      setAllMarkups(markupsData);
    } catch {
      /* ignore, nächster voller Reload holt es nach */
    }
  };

  // Beim ersten Wechsel in die Plan-Ansicht (oder wenn der zuvor gewählte
  // Plan nicht mehr existiert) einen sinnvollen Default wählen: bevorzugt
  // einen Plan, der mindestens eine (gefilterte) Pendenz referenziert.
  useEffect(() => {
    if (view !== "plan") return;
    if (planViewPlanId && plansWithVersions.some((pv) => pv.plan.id === planViewPlanId)) {
      return;
    }
    const withRefs = plansWithVersions.find(({ plan }) =>
      filtered.some((p) => (markupsByPendenz[p.id] || []).some((m) => m.planId === plan.id))
    );
    const target = withRefs || plansWithVersions[0];
    if (target) switchPlanView(target.plan.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, plansWithVersions]);

  // Filter – erledigte sind standardmässig ausgeblendet, wieder einblendbar
  const [filterArt, setFilterArt] = useState("");
  const [filterKategorie, setFilterKategorie] = useState("");
  const [filterSitzung, setFilterSitzung] = useState("");
  const [filterThema, setFilterThema] = useState("");
  const [showErledigt, setShowErledigt] = useState(false);
  const [search, setSearch] = useState("");

  // Kürzlich auf "erledigt" gesetzte Einträge bleiben 4s sichtbar (schnelle Rücknahme)
  const [recentlyErledigt, setRecentlyErledigt] = useState(() => new Set());
  const erledigtTimers = useRef({});

  useEffect(() => {
    const timers = erledigtTimers.current;
    return () => {
      Object.values(timers).forEach((t) => clearTimeout(t));
    };
  }, []);

  const loadData = useCallback(async () => {
    if (!projectUuid) return;
    try {
      setLoading(true);
      setError(null);
      const [
        pendData,
        partnersData,
        personsData,
        relationsData,
        teamData,
        catData,
        sitzungenData,
        markupsData,
        plansData,
      ] = await Promise.all([
        getPendenzen(projectUuid),
        getAllPartners(),
        getAllPersons(),
        getProjectRelations(projectCode),
        getAllTeamMembers(),
        getCategories(projectUuid),
        getSitzungen(projectUuid),
        getMarkupsForProject(projectUuid),
        getPlansWithVersions(projectUuid),
      ]);
      setPendenzen(pendData);
      setPartners(partnersData);
      setPersons(personsData);
      setRelations(relationsData);
      setTeamMembers(teamData);
      setCustomCategories(catData);
      setSitzungen(sitzungenData);
      setAllMarkups(markupsData);
      setPlansWithVersions(plansData);
      if (listKind === "project") {
        try {
          setAssignments(await getProjectAssignments(projectCode));
        } catch {
          setAssignments([]);
        }
      }
    } catch (err) {
      setError(err.message || "Die Pendenzen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [projectUuid, projectCode, listKind]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // "Meine Pendenzen" -> Projekt gewählt -> hierher navigiert: Formular öffnen.
  useEffect(() => {
    if (!createNewConsumed.current && location.state?.createNew && canEdit) {
      createNewConsumed.current = true;
      setShowForm(true);
    }
  }, [canEdit, location.state]);

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setForm(emptyForm());
    setShowForm(false);
  };

  // Öffnet das CRM-Modal zum Anlegen einer neuen Verantwortlichen. addFn hängt die
  // neue Options-ID an die auslösende Auswahl (Erfassen oder Bearbeiten) an.
  const handleCreateRequest = (createType, name, addFn) => {
    setCrmModal({ type: createType, name, addFn });
  };

  // Bestehenden CRM-Eintrag mit dem Projekt verbinden: öffnet das Zuordnungs-Modal
  // (Funktion + Öffentlich-Frage). Es wird kein neuer CRM-Eintrag angelegt.
  const handleLinkExisting = (option, addFn) => {
    setLinkModal({ option, addFn });
  };

  // Nach erfolgter Zuordnung: Relation ergänzen (Optionen rechnen neu) und die
  // nun zugeordnete Option in der auslösenden Auswahl selektieren.
  const handleLinked = (relation) => {
    if (relation) setRelations((prev) => [...prev, relation]);
    linkModal?.addFn?.(linkModal.option.id);
    setLinkModal(null);
  };

  // Nach erfolgreichem Anlegen: Rohdaten ergänzen (Optionen rechnen neu) und die
  // neue Option in der auslösenden Auswahl selektieren. Die Projekt-Relation macht
  // die Organisation/Person zugleich im Netzwerk-Tab sichtbar.
  const handleResponsibleCreated = ({ type, entity, relation, addSelect }) => {
    if (type === "person") {
      setPersons((prev) => [...prev, entity]);
    } else {
      setPartners((prev) => [...prev, entity]);
    }
    if (relation) setRelations((prev) => [...prev, relation]);
    addSelect?.();
    setCrmModal(null);
  };

  // --- Neue Pendenz anlegen ------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isRichTextEmpty(form.beschreibung)) {
      setError("Bitte eine Beschreibung erfassen.");
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await createPendenz(projectUuid, {
        ...form,
        beschreibung: form.beschreibung,
        verantwortlich: verantwortlichDisplay(
          form.verantwortlichIds,
          responsibleOptions
        ),
        verantwortlichExtern: verantwortlichDisplayExtern(
          form.verantwortlichIds,
          responsibleOptions
        ),
        // Im Sitzungsmodus wird jede neue Pendenz der laufenden Sitzung zugeordnet.
        sitzungId:
          sitzungModusActive && sitzungModusId ? sitzungModusId : form.sitzungId,
        erstelltVon: user?.email || user?.userDetails || "",
      });
      resetForm();
      await loadData();
    } catch (err) {
      setError(err.message || "Die Pendenz konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  };

  // --- Bearbeiten-Modal öffnen / schliessen --------------------------------
  const openEdit = (p) => {
    setEditForm({
      typ: p.typ,
      kategorie: p.kategorie,
      beschreibung: p.beschreibung || "",
      verantwortlichIds: p.verantwortlichIds || [],
      // Alt-Text aus US-PEND-001 nur als Fallback für ECHTE Altdaten (nie IDs
      // gehabt). Hatte die Pendenz IDs und werden diese geleert, darf NICHT der
      // interne Anzeigetext ("Malheur&Fortuna (Name)") zurückbleiben – sonst
      // landete der Klarname über computeVerantwortlichExtern in der externen Ansicht.
      verantwortlichFallback: p.verantwortlichIds?.length ? "" : p.verantwortlich || "",
      terminSoll: p.terminSoll || "",
      terminNeu: p.terminNeu || "",
      status: p.status || "offen",
      kommentar: p.kommentar || "",
      checkliste: p.checkliste || [],
      themen: p.themen || [],
      sichtbarExtern: !!p.sichtbarExtern,
      sitzungId: p.sitzungId || "",
    });
    setEditing(p);
  };

  // Öffnet eine Pendenz anhand ihrer ID im Bearbeiten-Modal (Plan-Ansicht:
  // Klick auf eine Markierung im Plan oder auf einen Listeneintrag kennt nur
  // die pendenzId, nicht das volle Objekt).
  const openEditById = (pendenzId) => {
    const p = pendenzen.find((x) => x.id === pendenzId);
    if (p) openEdit(p);
  };

  const closeEdit = () => {
    setEditing(null);
    setEditForm(null);
  };

  // --- Bearbeitete Pendenz speichern ---------------------------------------
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (isRichTextEmpty(editForm.beschreibung)) {
      setError("Bitte eine Beschreibung erfassen.");
      return;
    }
    const status = editForm.status;
    // Statuswechsel: erledigt -> erledigtAm setzen (bestehendes Datum behalten),
    // offen -> erledigtAm löschen.
    const erledigtAm =
      status === "erledigt" ? editing.erledigtAm || todayIso() : "";

    const updated = {
      ...editing,
      typ: editForm.typ,
      kategorie: editForm.kategorie,
      beschreibung: editForm.beschreibung,
      verantwortlichIds: editForm.verantwortlichIds,
      verantwortlich: computeVerantwortlich(
        editForm.verantwortlichIds,
        responsibleOptions,
        editForm.verantwortlichFallback
      ),
      verantwortlichExtern: computeVerantwortlichExtern(
        editForm.verantwortlichIds,
        responsibleOptions,
        editForm.verantwortlichFallback
      ),
      // terminSoll bleibt erhalten; ein neuer Termin landet in terminNeu.
      terminSoll: editForm.terminSoll,
      terminNeu: editForm.terminNeu,
      status,
      erledigtAm,
      kommentar: editForm.kommentar,
      checkliste: editForm.checkliste,
      themen: editForm.themen || [],
      sichtbarExtern: editForm.sichtbarExtern,
      sitzungId: editForm.sitzungId || "",
    };

    try {
      setSaving(true);
      setError(null);
      // Optimistisch aktualisieren (kein Seiten-Reload)
      setPendenzen((prev) =>
        prev.map((x) => (x.id === editing.id ? updated : x))
      );
      await updatePendenz(projectUuid, editing.id, updated);
      closeEdit();
    } catch (err) {
      setError(err.message || "Die Pendenz konnte nicht gespeichert werden.");
      await loadData();
    } finally {
      setSaving(false);
    }
  };

  // --- Pendenz löschen (mit Bestätigung) -----------------------------------
  const handleDelete = async () => {
    if (!editing) return;
    if (
      !window.confirm(
        `Pendenz Nr. ${editing.nummer} wirklich unwiderruflich löschen?`
      )
    ) {
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await deletePendenz(projectUuid, editing.id);
      setPendenzen((prev) => prev.filter((x) => x.id !== editing.id));
      closeEdit();
    } catch (err) {
      setError(err.message || "Die Pendenz konnte nicht gelöscht werden.");
    } finally {
      setSaving(false);
    }
  };

  // Checklistenpunkt direkt aus der Übersicht abhaken (optimistisch + persistiert)
  const handleToggleChecklistItem = async (p, itemId) => {
    const naechste = (p.checkliste || []).map((i) =>
      i.id === itemId ? { ...i, done: !i.done } : i
    );
    const updated = { ...p, checkliste: naechste };
    try {
      setError(null);
      setPendenzen((prev) => prev.map((x) => (x.id === p.id ? updated : x)));
      await updatePendenz(projectUuid, p.id, updated);
    } catch (err) {
      setError(err.message || "Die Checkliste konnte nicht aktualisiert werden.");
      await loadData();
    }
  };

  // Status direkt aus der Liste ändern. "erledigt" bleibt 4s sichtbar (ausgegraut),
  // damit es schnell wieder zurückgenommen werden kann, danach verschwindet es
  // (sofern erledigte nicht eingeblendet sind).
  const handleChangeStatus = async (p, naechsterStatus) => {
    const updated = {
      ...p,
      status: naechsterStatus,
      erledigtAm: naechsterStatus === "erledigt" ? p.erledigtAm || todayIso() : "",
    };
    setError(null);
    setPendenzen((prev) => prev.map((x) => (x.id === p.id ? updated : x)));

    // 4-Sekunden-Fenster für "erledigt" verwalten
    if (erledigtTimers.current[p.id]) {
      clearTimeout(erledigtTimers.current[p.id]);
      delete erledigtTimers.current[p.id];
    }
    if (naechsterStatus === "erledigt") {
      setRecentlyErledigt((prev) => new Set(prev).add(p.id));
      erledigtTimers.current[p.id] = setTimeout(() => {
        setRecentlyErledigt((prev) => {
          const next = new Set(prev);
          next.delete(p.id);
          return next;
        });
        delete erledigtTimers.current[p.id];
      }, 4000);
    } else {
      setRecentlyErledigt((prev) => {
        if (!prev.has(p.id)) return prev;
        const next = new Set(prev);
        next.delete(p.id);
        return next;
      });
    }

    try {
      await updatePendenz(projectUuid, p.id, updated);
    } catch (err) {
      setError(err.message || "Der Status konnte nicht geändert werden.");
      await loadData();
    }
  };

  // Kategorie einer Pendenz ändern (z.B. Drag&Drop im Board). "" = ohne Kategorie.
  const handleChangeCategory = async (p, catId) => {
    const kategorie = catId === "__none" ? "" : catId;
    if (kategorie === p.kategorie) return;
    const updated = { ...p, kategorie };
    setError(null);
    setPendenzen((prev) => prev.map((x) => (x.id === p.id ? updated : x)));
    try {
      await updatePendenz(projectUuid, p.id, updated);
    } catch (err) {
      setError(err.message || "Die Kategorie konnte nicht geändert werden.");
      await loadData();
    }
  };

  // Board: "+" je Spalte -> Erfassungsformular öffnen, vorbelegt mit dem Wert
  // der Spalte (Kategorie/Thema/Verantwortliche, je nach Gruppierung).
  const handleBoardAddCard = (columnKey) => {
    const base = emptyForm();
    if (columnKey && columnKey !== "__none") {
      if (boardGroupBy === "kategorie") base.kategorie = columnKey;
      else if (boardGroupBy === "thema") base.themen = [columnKey];
      else if (boardGroupBy === "verantwortlich") {
        const opt = responsibleOptions.find((o) => o.label === columnKey);
        if (opt) base.verantwortlichIds = [opt.id];
      }
    }
    if (sitzungModusActive && sitzungModusId) base.sitzungId = sitzungModusId;
    setForm(base);
    setShowForm(true);
  };

  // --- Kategorien (Board-Spalten) ------------------------------------------
  const handleAddCategory = async (label) => {
    try {
      setError(null);
      const cat = await createCategory(projectUuid, {
        label,
        sortOrder: customCategories.length + 1,
      });
      setCustomCategories((prev) => [...prev, cat]);
    } catch (err) {
      setError(err.message || "Die Kategorie konnte nicht angelegt werden.");
    }
  };

  const handleRenameCategory = async (id, label) => {
    try {
      setError(null);
      setCustomCategories((prev) =>
        prev.map((c) => (c.id === id ? { ...c, label } : c))
      );
      const cat = customCategories.find((c) => c.id === id);
      await updateCategory(projectUuid, id, {
        label,
        sortOrder: cat?.sortOrder || 0,
      });
    } catch (err) {
      setError(err.message || "Die Kategorie konnte nicht umbenannt werden.");
      await loadData();
    }
  };

  const handleDeleteCategory = async (id) => {
    if (
      !window.confirm(
        "Kategorie löschen? Zugeordnete Pendenzen erscheinen danach unter „Ohne Kategorie\"."
      )
    ) {
      return;
    }
    try {
      setError(null);
      await deleteCategory(projectUuid, id);
      setCustomCategories((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      setError(err.message || "Die Kategorie konnte nicht gelöscht werden.");
    }
  };

  // Client-seitige Filterung + Volltextsuche. Erledigte sind ausgeblendet, ausser
  // sie sind eingeblendet oder gerade erst (innerhalb 4s) erledigt worden.
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return pendenzen.filter((p) => {
      if (filterArt && p.typ !== filterArt) return false;
      if (filterKategorie && p.kategorie !== filterKategorie) return false;
      if (filterSitzung && p.sitzungId !== filterSitzung) return false;
      if (filterThema && !(p.themen || []).includes(filterThema)) return false;
      const inSitzung = sitzungModusActive && sitzungModusId;
      const istAktuelleSitzung = inSitzung && p.sitzungId === sitzungModusId;
      // Notizen (Art N) dienen der Protokollierung: in der allgemeinen Liste nur,
      // wenn "Notizen anzeigen" aktiv ist – in der laufenden Sitzung immer sichtbar.
      if (p.typ === "N" && !showNotizen && !istAktuelleSitzung) return false;
      // Sitzungsmodus: nur Pendenzen der laufenden Sitzung – ausser "Vergangene
      // einblenden" oder aktive Suche (dann sind frühere durchsuchbar).
      if (inSitzung && !sitzungModusShowPast && !term && !istAktuelleSitzung) {
        return false;
      }
      if (
        p.status === "erledigt" &&
        !showErledigt &&
        !recentlyErledigt.has(p.id)
      ) {
        return false;
      }
      if (term) {
        const haystack = `${richTextToPlainText(p.beschreibung)} ${p.verantwortlich} ${(p.themen || []).join(" ")}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [pendenzen, filterArt, filterKategorie, filterSitzung, filterThema, showErledigt, recentlyErledigt, search, sitzungModusActive, sitzungModusId, sitzungModusShowPast, showNotizen]);

  // --- Multiselect/Multiedit -------------------------------------------------
  const allFilteredSelected =
    filtered.length > 0 && filtered.every((p) => bulkSelected.has(p.id));
  const toggleBulk = (id) =>
    setBulkSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleBulkAll = () =>
    setBulkSelected((prev) =>
      filtered.every((p) => prev.has(p.id))
        ? new Set()
        : new Set(filtered.map((p) => p.id))
    );
  const clearBulk = () => setBulkSelected(new Set());
  // Checkbox-Spalte nur im Multi-Modus (und mit Bearbeitungsrecht) einblenden.
  const showCheck = !readOnly && multiMode;
  const toggleMultiMode = () =>
    setMultiMode((v) => {
      if (v) clearBulk();
      return !v;
    });

  // --- Sitzungsmodus ---------------------------------------------------------
  const defaultSitzungForm = () => {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, "0");
    const hh1 = String((now.getHours() + 1) % 24).padStart(2, "0");
    return {
      art: SITZUNG_ARTEN[0],
      nr: suggestNextNr(sitzungen, SITZUNG_ARTEN[0]),
      datum: todayIso(),
      vonZeit: `${hh}:00`,
      bisZeit: `${hh1}:00`,
      anwesende: [],
    };
  };
  const sitzungFormChange = (field, value) =>
    setSitzungForm((prev) => ({ ...prev, [field]: value }));

  // Sitzungsmodus starten: Die Sitzung wird SOFORT angelegt (sitzungModusId sofort
  // gesetzt), damit jede erfasste Pendenz zugeordnet ist – kein "Lücken"-Zustand
  // vor einem separaten Start, in dem Pendenzen ohne Zuordnung verloren gingen.
  // Das Setup-Formular bearbeitet danach die bereits laufende Sitzung.
  const enterSitzungModus = async () => {
    if (sitzungModusSaving) return;
    const base = defaultSitzungForm();
    setSitzungForm(base);
    setSitzungModusActive(true);
    setShowSitzungSetup(true);
    setSitzungModusShowPast(false);
    setSitzungModusId(null);
    try {
      setSitzungModusSaving(true);
      setError(null);
      const created = await createSitzung(
        projectUuid,
        {
          art: base.art,
          nr: base.nr,
          datum: base.datum,
          vonZeit: base.vonZeit,
          bisZeit: base.bisZeit,
          ort: "",
          anwesende: base.anwesende,
        },
        user?.email || user?.userDetails || ""
      );
      setSitzungen((prev) => [created, ...prev]);
      setSitzungModusId(created.id);
    } catch (err) {
      setError(err.message || "Die Sitzung konnte nicht gestartet werden.");
      // Anlegen fehlgeschlagen -> Sitzungsmodus wieder verlassen.
      setSitzungModusActive(false);
      setShowSitzungSetup(false);
      setSitzungForm(null);
    } finally {
      setSitzungModusSaving(false);
    }
  };

  // "Sitzungsmodus starten" öffnet zuerst eine kleine Auswahl: neue Sitzung
  // ODER eine bestehende (auch vergangene) Sitzung weiterbearbeiten. Erst nach
  // der Auswahl passiert etwas – ein Abbrechen hier hat keinerlei Seiteneffekt.
  const openSitzungModusChooser = () => {
    setSitzungResumeSelection("");
    setSitzungModusChooserOpen(true);
  };

  const cancelSitzungModusChooser = () => {
    setSitzungModusChooserOpen(false);
    setSitzungResumeSelection("");
  };

  const confirmSitzungModusChooser = () => {
    setSitzungModusChooserOpen(false);
    if (sitzungResumeSelection) {
      resumeSitzungModus(sitzungResumeSelection);
    } else {
      enterSitzungModus();
    }
  };

  // Eine bestehende Sitzung (auch vergangen/abgeschlossen) zum Weiterbearbeiten
  // wieder öffnen: keine neue Sitzung anlegen, sondern an die vorhandene
  // andocken. Zugeordnete Pendenzen UND eine ggf. hochgeladene Präsentation
  // (Folien) erscheinen dadurch automatisch wieder (beide hängen nur an der
  // sitzungId, nicht an einem separaten "aktiv"-Flag).
  const resumeSitzungModus = (sitzungId) => {
    const sitzung = sitzungen.find((s) => s.id === sitzungId);
    if (!sitzung) return;
    setSitzungForm({
      art: sitzung.art,
      nr: sitzung.nr,
      datum: sitzung.datum,
      vonZeit: sitzung.vonZeit || "",
      bisZeit: sitzung.bisZeit || "",
      anwesende: sitzung.anwesende || [],
    });
    setSitzungModusActive(true);
    setShowSitzungSetup(false);
    setSitzungModusShowPast(false);
    setSitzungModusId(sitzung.id);
  };

  // Sitzungsmodus beenden: eine ohne zugeordnete Pendenzen gebliebene (leere)
  // Sitzung wird wieder gelöscht, damit kein Datenmüll entsteht.
  const leaveSitzungModus = async () => {
    const id = sitzungModusId;
    setSitzungModusActive(false);
    setShowSitzungSetup(false);
    setSitzungModusShowPast(false);
    setSitzungForm(null);
    setSitzungModusId(null);
    if (!id) return;
    if (!pendenzen.some((p) => p.sitzungId === id)) {
      try {
        // deleteSitzung räumt seit US-SITZ-003 auch eine eventuell hochgeladene
        // Präsentation dieser Sitzung mit ab (siehe azureSitzungenService.js).
        await deleteSitzung(projectUuid, id);
        setSitzungen((prev) => prev.filter((s) => s.id !== id));
      } catch {
        /* ignore – leere Sitzung bleibt bestehen, unkritisch */
      }
    } else {
      // Sitzung bleibt bestehen: nur Folien OHNE zugeordnete Pendenz entfernen.
      // Die Zuordnung genutzter Folien bleibt dabei unangetastet (US-SITZ-003,
      // Nutzerwunsch 2026-09-14).
      try {
        await pruneUnusedFolien(projectUuid, id);
      } catch {
        /* ignore – ungenutzte Folien bleiben dann halt liegen, unkritisch */
      }
    }
  };

  // Sitzung starten (persistieren) bzw. laufende Sitzung aktualisieren.
  const saveSitzung = async () => {
    if (!sitzungForm?.art?.trim()) {
      setError("Bitte eine Art erfassen.");
      return;
    }
    const payload = {
      art: sitzungForm.art,
      nr: sitzungForm.nr,
      datum: sitzungForm.datum,
      vonZeit: sitzungForm.vonZeit,
      bisZeit: sitzungForm.bisZeit,
      ort: "",
      anwesende: sitzungForm.anwesende || [],
    };
    try {
      setSitzungModusSaving(true);
      setError(null);
      if (sitzungModusId) {
        // Bestehende Sitzung mergen (voller PUT), damit erstelltAm/Von erhalten bleiben.
        const updated = await updateSitzung(projectUuid, sitzungModusId, {
          ...(sitzungModusSitzung || {}),
          ...payload,
        });
        setSitzungen((prev) => prev.map((s) => (s.id === sitzungModusId ? updated : s)));
      } else {
        const created = await createSitzung(
          projectUuid,
          payload,
          user?.email || user?.userDetails || ""
        );
        setSitzungen((prev) => [created, ...prev]);
        setSitzungModusId(created.id);
      }
      setShowSitzungSetup(false);
    } catch (err) {
      setError(err.message || "Die Sitzung konnte nicht gespeichert werden.");
    } finally {
      setSitzungModusSaving(false);
    }
  };

  const sitzungModusSitzung = sitzungModusId
    ? sitzungen.find((s) => s.id === sitzungModusId) || null
    : null;

  // --- Inline-Schnellerfassung (leere Zeile zuunterst) -----------------------
  const quickChange = (field, value) =>
    setQuickAdd((prev) => ({ ...prev, [field]: value }));

  // Kategorie-Default = die des Eintrags direkt darüber (letzter gefilterter),
  // sonst "Org". null in quickAdd bedeutet "erben".
  const quickKategorie =
    quickAdd.kategorie ??
    (filtered.length ? filtered[filtered.length - 1].kategorie : "Org");

  const commitQuickAdd = async () => {
    if (isRichTextEmpty(quickAdd.beschreibung) || quickSaving) return;
    const data = quickAdd;
    setQuickSaving(true);
    try {
      setError(null);
      await createPendenz(projectUuid, {
        typ: data.typ,
        kategorie: quickKategorie,
        beschreibung: data.beschreibung,
        verantwortlichIds: data.verantwortlichIds || [],
        verantwortlich: verantwortlichDisplay(
          data.verantwortlichIds || [],
          responsibleOptions
        ),
        verantwortlichExtern: verantwortlichDisplayExtern(
          data.verantwortlichIds || [],
          responsibleOptions
        ),
        terminSoll: data.terminSoll,
        terminNeu: "",
        status: data.status,
        kommentar: "",
        checkliste: [],
        themen: [],
        sichtbarExtern: true,
        sitzungId: sitzungModusActive && sitzungModusId ? sitzungModusId : "",
        erstelltVon: user?.email || user?.userDetails || "",
      });
      setQuickAdd({
        typ: "P",
        kategorie: null,
        beschreibung: "",
        verantwortlichIds: [],
        terminSoll: todayIso(),
        status: "offen",
      });
      // RichTextEditor neu mounten, damit die Beschreibung wirklich leer ist
      // (Tiptap übernimmt `content` sonst nur beim ersten Mount).
      setQuickAddEditorKey((k) => k + 1);
      // Gezielt nur die Pendenzen neu laden (kein globaler Lade-Blitz, damit die
      // Inline-Erfassung flüssig bleibt).
      setPendenzen(await getPendenzen(projectUuid));
    } catch (err) {
      setError(err.message || "Die Pendenz konnte nicht gespeichert werden.");
    } finally {
      setQuickSaving(false);
    }
  };

  // Committen, sobald der Fokus die Zeile ganz verlässt (Tab aus dem letzten Feld
  // oder Klick woanders hin) – nicht beim Tabben zwischen den Feldern der Zeile.
  const handleQuickRowBlur = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    commitQuickAdd();
  };

  // Sammeländerung: nur die im Modal angetippten Felder (changes) überschreiben
  // alle markierten Pendenzen. verantwortlichIds in changes -> Anzeige-Strings neu
  // berechnen; status -> erledigtAm konsistent setzen.
  const handleBulkApply = async (changes) => {
    const ids = Array.from(bulkSelected).filter((id) =>
      pendenzen.some((p) => p.id === id)
    );
    if (ids.length === 0) {
      setShowBulkEdit(false);
      return;
    }
    try {
      setSaving(true);
      setError(null);
      for (const id of ids) {
        const p = pendenzen.find((x) => x.id === id);
        if (!p) continue;
        const merged = { ...p, ...changes };
        if ("verantwortlichIds" in changes) {
          merged.verantwortlich = verantwortlichDisplay(
            changes.verantwortlichIds,
            responsibleOptions
          );
          merged.verantwortlichExtern = verantwortlichDisplayExtern(
            changes.verantwortlichIds,
            responsibleOptions
          );
        }
        if ("status" in changes) {
          merged.erledigtAm =
            changes.status === "erledigt" ? p.erledigtAm || todayIso() : "";
        }
        // Sequenziell, um die Table-API nicht zu überlasten.
        // eslint-disable-next-line no-await-in-loop
        await updatePendenz(projectUuid, id, merged);
      }
      setShowBulkEdit(false);
      setBulkSelected(new Set());
      await loadData();
    } catch (err) {
      setError(err.message || "Die Sammeländerung ist fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  // Board-Spalten (Kategorien) und Zuordnung der (gefilterten) Pendenzen.
  // Orphan-Einträge (Kategorie existiert nicht mehr) landen unter "Ohne Kategorie".
  const { boardColumns, boardItems } = useMemo(() => {
    // Kategorie: feste Spalten (mit DnD + Spaltenverwaltung).
    if (boardGroupBy === "kategorie") {
      const byCol = {};
      categories.forEach((c) => (byCol[c.id] = []));
      let hasOrphans = false;
      filtered.forEach((p) => {
        if (byCol[p.kategorie]) byCol[p.kategorie].push(p);
        else {
          (byCol.__none = byCol.__none || []).push(p);
          hasOrphans = true;
        }
      });
      const cols = categories.map((c) => ({ key: c.id, label: c.label, fixed: c.fixed }));
      if (hasOrphans) cols.push({ key: "__none", label: "Ohne Kategorie", fixed: true });
      return { boardColumns: cols, boardItems: byCol };
    }
    // Thema / Zuständigkeit: gruppieren nach dem ERSTEN Wert (reine Ansicht).
    const keyOf = (p) => {
      if (boardGroupBy === "thema") return (p.themen && p.themen[0]) || "__none";
      if (p.verantwortlichIds && p.verantwortlichIds.length) {
        const o = responsibleOptions.find((x) => x.id === p.verantwortlichIds[0]);
        if (o) return o.label;
      }
      if (p.verantwortlich) return p.verantwortlich.split(",")[0].trim();
      return "__none";
    };
    const byCol = {};
    filtered.forEach((p) => {
      const k = keyOf(p);
      (byCol[k] = byCol[k] || []).push(p);
    });
    const named = Object.keys(byCol)
      .filter((k) => k !== "__none")
      .sort((a, b) => a.localeCompare(b));
    const cols = named.map((k) => ({ key: k, label: k, fixed: true }));
    if (byCol.__none) {
      cols.push({
        key: "__none",
        label: boardGroupBy === "thema" ? "Ohne Thema" : "Ohne Zuständigkeit",
        fixed: true,
      });
    }
    return { boardColumns: cols, boardItems: byCol };
  }, [boardGroupBy, categories, filtered, responsibleOptions]);

  // --- Excel-Export der aktuell gefilterten Liste (US-PEND-003) -------------
  const handleExport = async () => {
    try {
      setExporting(true);
      setError(null);
      await exportPendenzenToExcel({
        pendenzen: filtered,
        projectCode,
      });
    } catch (err) {
      console.error("Pendenzen-Export fehlgeschlagen:", err);
      setError("Der Excel-Export ist fehlgeschlagen.");
    } finally {
      setExporting(false);
    }
  };

  // Anzahl der aktuell als extern sichtbar markierten Einträge
  const externCount = useMemo(
    () => pendenzen.filter((p) => p.sichtbarExtern).length,
    [pendenzen]
  );

  // --- Teilen-Dialog (US-PEND-004) -----------------------------------------
  const loadShares = useCallback(async () => {
    try {
      setShareLoading(true);
      setShareError(null);
      const data = await getSharesForProject(projectUuid);
      setShares(data);
    } catch (err) {
      setShareError(err.message || "Die Freigabe-Links konnten nicht geladen werden.");
    } finally {
      setShareLoading(false);
    }
  }, [projectUuid]);

  const openShareDialog = () => {
    setShowShare(true);
    setNewExpiresAt("");
    setCopiedToken(null);
    loadShares();
  };

  const handleCreateShare = async () => {
    try {
      setShareBusy(true);
      setShareError(null);
      await createShare(projectUuid, {
        createdVon: user?.email || user?.userDetails || "",
        expiresAt: newExpiresAt || "",
      });
      setNewExpiresAt("");
      await loadShares();
    } catch (err) {
      setShareError(err.message || "Der Freigabe-Link konnte nicht erstellt werden.");
    } finally {
      setShareBusy(false);
    }
  };

  const handleRevokeShare = async (token) => {
    if (!window.confirm("Diesen Freigabe-Link wirklich widerrufen?")) return;
    try {
      setShareBusy(true);
      setShareError(null);
      await revokeShare(token);
      await loadShares();
    } catch (err) {
      setShareError(err.message || "Der Freigabe-Link konnte nicht widerrufen werden.");
    } finally {
      setShareBusy(false);
    }
  };

  const handleCopyLink = async (share) => {
    try {
      await navigator.clipboard.writeText(share.url);
      setCopiedToken(share.token);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch {
      setShareError("Der Link konnte nicht in die Zwischenablage kopiert werden.");
    }
  };

  if (loading) {
    return (
      <div className="pendenzen pendenzen--loading">
        Pendenzen werden geladen...
      </div>
    );
  }

  return (
    <div className={`pendenzen${isFullscreen ? " pendenzen--fullscreen" : ""}`}>
      <div className="pendenzen__header">
        <div>
          {onBack && (
            <button
              type="button"
              className="pendenzen__back"
              onClick={onBack}
            >
              ← Zurück zur Liste
            </button>
          )}
          <h3>{projectTitle || "Pendenzen, Entscheide & Notizen"}</h3>
        </div>
        <div className="pendenzen__header-actions">
          <div className="pendenzen__viewtoggle">
            <button
              type="button"
              className={`pendenzen__viewbtn${view === "list" ? " is-active" : ""}`}
              onClick={() => setView("list")}
            >
              Liste
            </button>
            <button
              type="button"
              className={`pendenzen__viewbtn${view === "board" ? " is-active" : ""}`}
              onClick={() => setView("board")}
            >
              Board
            </button>
            <button
              type="button"
              className={`pendenzen__viewbtn${view === "plan" ? " is-active" : ""}`}
              onClick={() => setView("plan")}
            >
              Plan
            </button>
          </div>
          <button
            type="button"
            className="pendenzen__btn"
            onClick={() => setIsFullscreen((v) => !v)}
          >
            {isFullscreen ? "Vollbild beenden" : "Vollbild"}
          </button>
          {view === "list" && canEdit && (
            <button
              type="button"
              className={`pendenzen__btn${multiMode ? " pendenzen__btn--primary" : ""}`}
              onClick={toggleMultiMode}
              title="Mehrere Pendenzen auswählen und gemeinsam bearbeiten"
            >
              Multi
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              className={`pendenzen__btn${sitzungModusActive ? " pendenzen__btn--primary" : ""}`}
              onClick={() =>
                sitzungModusActive
                  ? leaveSitzungModus()
                  : openSitzungModusChooser()
              }
              title="Fokussiert Pendenzen einer Sitzung erfassen"
            >
              {sitzungModusActive ? "Sitzungsmodus beenden" : "Sitzungsmodus"}
            </button>
          )}
          {listKind === "project" && (
            <button
              type="button"
              className="pendenzen__btn"
              onClick={() => setShowPlaeneModal(true)}
              title="Pläne dieses Projekts verwalten"
            >
              Pläne
            </button>
          )}
          {listKind === "project" && canEdit && (
            <button
              type="button"
              className="pendenzen__btn"
              onClick={() => setShowMembers(true)}
              title="Team-Mitglieder dem Projekt zuordnen"
            >
              Personen zuordnen
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              className="pendenzen__btn"
              onClick={openShareDialog}
              title="Read-only-Link für externe Beteiligte verwalten"
            >
              Extern teilen
            </button>
          )}
          <button
            type="button"
            className="pendenzen__btn"
            onClick={handleExport}
            disabled={exporting || filtered.length === 0}
            title={
              filtered.length === 0
                ? "Keine Einträge zum Exportieren"
                : "Sichtbare Einträge als Excel exportieren"
            }
          >
            {exporting ? "Export..." : "Export"}
          </button>
          {canEdit && listKind === "project" && (
            <button
              type="button"
              className="pendenzen__btn"
              onClick={() => setShowNeuePlanpendenz(true)}
              disabled={plansWithVersions.length === 0}
              title={
                plansWithVersions.length === 0
                  ? 'Es sind noch keine Pläne vorhanden. Bitte zuerst im Tab "Pläne" einen Plan hochladen.'
                  : "Pendenz direkt auf einem Plan platzieren"
              }
            >
              + Neue Planpendenz
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              className="pendenzen__btn pendenzen__btn--primary"
              onClick={() => setShowForm((v) => !v)}
            >
              + Neue Pendenz
            </button>
          )}
        </div>
      </div>
      {readOnly && (
        <div className="pendenzen__readonly-hint">
          Nur-Lese-Ansicht – Sie sind diesem Projekt nicht zugeordnet.
        </div>
      )}

      {error && (
        <div
          className="pendenzen__error"
          role="alert"
          onClick={() => setError(null)}
        >
          {error}
        </div>
      )}

      {!sitzungModusActive && sitzungModusChooserOpen && (
        <div className="pendenzen__sitzungpanel">
          <div className="pendenzen__sitzungpanel-head">
            <h4>Sitzungsmodus starten</h4>
            <p className="pendenzen__sitzungpanel-hint">
              Neue Sitzung anlegen oder eine bestehende Sitzung weiterbearbeiten
              – inkl. ihrer Folien und bereits erfassten Pendenzen, falls
              vorhanden.
            </p>
          </div>
          <div className="pendenzen__form-grid">
            <label className="pendenzen__field pendenzen__field--full">
              <span>Sitzung</span>
              <select
                value={sitzungResumeSelection}
                onChange={(e) => setSitzungResumeSelection(e.target.value)}
              >
                <option value="">— Neue Sitzung —</option>
                {sitzungen.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.art} Nr. {s.nr} · {formatDate(s.datum)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="pendenzen__form-actions">
            <button
              type="button"
              className="pendenzen__btn"
              onClick={cancelSitzungModusChooser}
            >
              Abbrechen
            </button>
            <button
              type="button"
              className="pendenzen__btn pendenzen__btn--primary"
              onClick={confirmSitzungModusChooser}
              disabled={sitzungModusSaving}
            >
              {sitzungResumeSelection ? "Sitzung öffnen" : "Neue Sitzung starten"}
            </button>
          </div>
        </div>
      )}

      {sitzungModusActive && sitzungForm && (
        <div className="pendenzen__sitzungpanel">
          {showSitzungSetup || !sitzungModusId ? (
            <>
              <div className="pendenzen__sitzungpanel-head">
                <h4>Sitzungsdetails</h4>
                <p className="pendenzen__sitzungpanel-hint">
                  Die Sitzung läuft bereits – hier Datum/Zeit/Teilnehmer:innen
                  anpassen. Neu erfasste Pendenzen werden ihr automatisch zugeordnet.
                </p>
              </div>
              <div className="pendenzen__form-grid">
                <label className="pendenzen__field">
                  <span>Art</span>
                  <input
                    type="text"
                    list="sitzungsarten-modus"
                    value={sitzungForm.art}
                    onChange={(e) => {
                      sitzungFormChange("art", e.target.value);
                      sitzungFormChange("nr", suggestNextNr(sitzungen, e.target.value));
                    }}
                  />
                  <datalist id="sitzungsarten-modus">
                    {SITZUNG_ARTEN.map((a) => (
                      <option key={a} value={a} />
                    ))}
                  </datalist>
                </label>
                <label className="pendenzen__field">
                  <span>Nummer</span>
                  <input
                    type="text"
                    value={sitzungForm.nr}
                    onChange={(e) => sitzungFormChange("nr", e.target.value)}
                  />
                </label>
                <label className="pendenzen__field">
                  <span>Datum</span>
                  <input
                    type="date"
                    value={sitzungForm.datum}
                    onChange={(e) => sitzungFormChange("datum", e.target.value)}
                  />
                </label>
                <label className="pendenzen__field">
                  <span>Von</span>
                  <input
                    type="time"
                    value={sitzungForm.vonZeit}
                    onChange={(e) => sitzungFormChange("vonZeit", e.target.value)}
                  />
                </label>
                <label className="pendenzen__field">
                  <span>Bis</span>
                  <input
                    type="time"
                    value={sitzungForm.bisZeit}
                    onChange={(e) => sitzungFormChange("bisZeit", e.target.value)}
                  />
                </label>
                <div className="pendenzen__field pendenzen__field--full">
                  <span>Teilnehmer:innen</span>
                  <AttendeeSelect
                    options={responsibleOptions}
                    attendees={sitzungForm.anwesende || []}
                    onChange={(a) => sitzungFormChange("anwesende", a)}
                  />
                </div>
              </div>
              <div className="pendenzen__form-actions">
                <button
                  type="button"
                  className="pendenzen__btn"
                  onClick={leaveSitzungModus}
                  disabled={sitzungModusSaving}
                  title="Sitzungsmodus beenden (leere Sitzung wird verworfen)"
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="pendenzen__btn pendenzen__btn--primary"
                  onClick={saveSitzung}
                  disabled={sitzungModusSaving || !sitzungModusId}
                >
                  {sitzungModusSaving ? "Speichern..." : "Übernehmen"}
                </button>
              </div>
            </>
          ) : (
            <div className="pendenzen__sitzungpanel-active">
              <span className="pendenzen__sitzungpanel-info">
                <strong>Sitzung läuft:</strong> {sitzungModusSitzung?.art} Nr.{" "}
                {sitzungModusSitzung?.nr} · {formatDate(sitzungModusSitzung?.datum)}
                {sitzungModusSitzung?.vonZeit
                  ? ` · ${sitzungModusSitzung.vonZeit}–${sitzungModusSitzung.bisZeit}`
                  : ""}
                {` · ${(sitzungModusSitzung?.anwesende || []).length} Teilnehmer:innen`}
              </span>
              <label className="pendenzen__filter-check">
                <input
                  type="checkbox"
                  checked={sitzungModusShowPast}
                  onChange={(e) => setSitzungModusShowPast(e.target.checked)}
                />
                <span>Vergangene einblenden</span>
              </label>
              <button
                type="button"
                className="pendenzen__btn"
                onClick={() => setShowSitzungSetup(true)}
              >
                Details / Teilnehmer
              </button>
              <button
                type="button"
                className="pendenzen__btn"
                onClick={() => setShowProtokollModal(true)}
              >
                Protokoll
              </button>
              <button
                type="button"
                className="pendenzen__btn"
                onClick={leaveSitzungModus}
              >
                Sitzungsmodus beenden
              </button>
            </div>
          )}
        </div>
      )}

      {showProtokollModal && sitzungModusSitzung && (
        <ProtokollModal
          sitzung={sitzungModusSitzung}
          // `projectTitle` ist hier bereits "<Code> – <Titel>" (fuer die
          // Seitenueberschrift vorformatiert, siehe PendenzenProjectWrapper.jsx)
          // — Excel-/PDF-Export haengen den Code aber selbst nochmals vor
          // `project.title` an ("Projekt: <Code> – <title>"). Ohne diesen
          // Rueckbau erschien der Code doppelt ("026 – 026 – ..."), da der
          // Sitzungen-Tab (Sitzungen.jsx) denselben Export mit dem rohen,
          // unpraefigierten Projekt-Titel aufruft und dort korrekt aussieht.
          project={{
            id: projectUuid,
            code: projectCode,
            title:
              projectTitle && projectCode && projectTitle.startsWith(`${projectCode} – `)
                ? projectTitle.slice(`${projectCode} – `.length)
                : projectTitle,
          }}
          pendenzen={pendenzen}
          sitzungen={sitzungen}
          sources={{ partners, persons, teamMembers }}
          options={responsibleOptions}
          onClose={() => setShowProtokollModal(false)}
        />
      )}

      {sitzungModusActive && sitzungModusId && (
        <SitzungPraesentation
          sitzungId={sitzungModusId}
          projectUuid={projectUuid}
          pendenzen={pendenzen}
          responsibleOptions={responsibleOptions}
          crmOptions={crmOptions}
          onCreateRequest={handleCreateRequest}
          onLinkExisting={handleLinkExisting}
          onDataChanged={refreshAfterPlanViewChange}
          onOpenPendenz={openEdit}
          readOnly={readOnly}
        />
      )}

      {showForm && (
        <form className="pendenzen__form" onSubmit={handleSubmit}>
          <PendenzFields
            form={form}
            onChange={handleFormChange}
            options={responsibleOptions}
            onCreateRequest={handleCreateRequest}
            crmOptions={crmOptions}
            onLinkExisting={handleLinkExisting}
            categoryOptions={categories}
            sitzungen={sitzungen}
            onCreateSitzungRequest={() => setSitzungModal({ target: "form" })}
            themenSuggestions={allThemen}
          />
          <div className="pendenzen__form-actions">
            <button
              type="button"
              className="pendenzen__btn"
              onClick={resetForm}
              disabled={saving}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="pendenzen__btn pendenzen__btn--primary"
              disabled={saving}
            >
              {saving ? "Speichern..." : "Speichern"}
            </button>
          </div>
        </form>
      )}

      <div className="pendenzen__filters">
        <input
          type="search"
          className="pendenzen__search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Suche in Beschreibung / Verantwortlich..."
        />
        <select value={filterArt} onChange={(e) => setFilterArt(e.target.value)}>
          <option value="">Alle Arten</option>
          {ART_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          value={filterKategorie}
          onChange={(e) => setFilterKategorie(e.target.value)}
        >
          <option value="">Alle Kategorien</option>
          {categories.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          value={filterSitzung}
          onChange={(e) => setFilterSitzung(e.target.value)}
        >
          <option value="">Alle Sitzungen</option>
          {sitzungen.map((s) => (
            <option key={s.id} value={s.id}>
              {s.art} Nr. {s.nr} ({s.datum})
            </option>
          ))}
        </select>
        {allThemen.length > 0 && (
          <select value={filterThema} onChange={(e) => setFilterThema(e.target.value)}>
            <option value="">Alle Themen</option>
            {allThemen.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        )}
        <label className="pendenzen__filter-check">
          <input
            type="checkbox"
            checked={showErledigt}
            onChange={(e) => setShowErledigt(e.target.checked)}
          />
          <span>Erledigte anzeigen</span>
        </label>
        <label className="pendenzen__filter-check">
          <input
            type="checkbox"
            checked={showNotizen}
            onChange={(e) => setShowNotizen(e.target.checked)}
          />
          <span>Notizen anzeigen</span>
        </label>
      </div>

      {view === "list" && showCheck && bulkSelected.size > 0 && (
        <div className="pendenzen__bulkbar">
          <span>{bulkSelected.size} ausgewählt</span>
          <button
            type="button"
            className="pendenzen__btn pendenzen__btn--primary"
            onClick={() => setShowBulkEdit(true)}
          >
            Bearbeiten
          </button>
          <button type="button" className="pendenzen__btn" onClick={clearBulk}>
            Auswahl aufheben
          </button>
        </div>
      )}

      {view === "list" && (
      <div className="pendenzen__table-wrapper">
        <table className="pendenzen__table">
          <thead>
            <tr>
              {showCheck && (
                <th className="col-check">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={toggleBulkAll}
                    title="Alle (gefilterten) auswählen"
                  />
                </th>
              )}
              <th className="col-nr">Nr</th>
              <th className="col-art">Art</th>
              <th className="col-beschreibung">Beschreibung</th>
              <th className="col-verantwortlich">Verantwortlich</th>
              <th className="col-termin">Deadline</th>
              <th className="col-termin">neue Deadline</th>
              <th className="col-status">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && readOnly && (
              <tr>
                <td className="pendenzen__empty" colSpan={showCheck ? 8 : 7}>
                  Keine Einträge für die aktuelle Auswahl.
                </td>
              </tr>
            )}
            {filtered.map((p) => {
                const ueberfaellig = istUeberfaellig(p);
                const erledigt = p.status === "erledigt";
                const checkliste = p.checkliste || [];
                return (
                  <React.Fragment key={p.id}>
                  <tr
                    className={`pendenzen__row${erledigt ? " row-erledigt" : ""}${
                      checkliste.length > 0 ? " has-checklist" : ""
                    }`}
                    onClick={() => openEdit(p)}
                    title="Zum Bearbeiten anklicken"
                  >
                    {showCheck && (
                      <td
                        className="col-check"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={bulkSelected.has(p.id)}
                          onChange={() => toggleBulk(p.id)}
                          aria-label={`Pendenz Nr. ${p.nummer} auswählen`}
                        />
                      </td>
                    )}
                    <td className="col-nr">{p.nummer}</td>
                    <td className="col-art">
                      <span
                        className={`art-badge art-badge--${p.typ}`}
                        title={artLabel(p.typ)}
                      >
                        {p.typ}
                      </span>
                    </td>
                    <td className="col-beschreibung">
                      <span className="kategorie-chip">
                        {kategorieLabelFor(p.kategorie) || "Ohne Kategorie"}
                      </span>
                      {(p.themen || []).map((t) => (
                        <span key={t} className="thema-chip">
                          {t}
                        </span>
                      ))}
                      <span
                        className="beschreibung-text rte-content"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: sanitizeRichText(p.beschreibung) }}
                      />
                      {!p.sichtbarExtern && (
                        <span className="intern-badge" title="Nur intern sichtbar">
                          intern
                        </span>
                      )}
                      {(markupsByPendenz[p.id]?.length || 0) > 0 && (
                        <button
                          type="button"
                          className={`plan-badge${
                            expandedPlanPendenzId === p.id ? " is-active" : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpandPlan(p.id);
                          }}
                          title="Verortung im Plan anzeigen"
                        >
                          Plan ({markupsByPendenz[p.id].length})
                        </button>
                      )}
                    </td>
                    <td className="col-verantwortlich">{p.verantwortlich}</td>
                    <td
                      className={`col-termin${
                        ueberfaellig && !p.terminNeu ? " termin-ueberfaellig" : ""
                      }`}
                    >
                      {formatDate(p.terminSoll)}
                    </td>
                    <td
                      className={`col-termin${
                        ueberfaellig && p.terminNeu ? " termin-ueberfaellig" : ""
                      }`}
                    >
                      {formatDate(p.terminNeu)}
                    </td>
                    <td className="col-status">
                      {readOnly ? (
                        <span className={`status-badge status-badge--${p.status}`}>
                          {STATUS_OPTIONS.find((o) => o.value === p.status)?.label ||
                            p.status}
                        </span>
                      ) : (
                        <select
                          className={`status-select status-select--${p.status}`}
                          value={p.status}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleChangeStatus(p, e.target.value);
                          }}
                          title="Status ändern"
                        >
                          {STATUS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                  </tr>
                  {checkliste.length > 0 && (
                    <tr
                      className={`pendenzen__checklist-row${
                        erledigt ? " row-erledigt" : ""
                      }`}
                    >
                      <td colSpan={showCheck ? 3 : 2}></td>
                      <td colSpan={5}>
                        <ul className="checklist__items checklist__items--compact">
                          {checkliste.map((item) => (
                            <li
                              key={item.id}
                              className={`checklist__item${
                                item.done ? " is-done" : ""
                              }`}
                            >
                              <button
                                type="button"
                                className="checklist__toggle"
                                disabled={readOnly}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!readOnly) handleToggleChecklistItem(p, item.id);
                                }}
                                title={
                                  item.done
                                    ? "Als offen markieren"
                                    : "Als erledigt markieren"
                                }
                              >
                                {item.done ? "●" : "○"}
                              </button>
                              <span className="checklist__text">{item.text}</span>
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  )}
                  {expandedPlanPendenzId === p.id && (
                    <tr className="pendenzen__plan-row">
                      <td colSpan={showCheck ? 8 : 7}>
                        <PendenzPlanPanel
                          markups={markupsByPendenz[p.id] || []}
                          plansWithVersions={plansWithVersions}
                          project={{ id: projectUuid, code: projectCode }}
                          activeGroupKey={activeGroupKey}
                          onActiveGroupChange={setActiveGroupKey}
                          focusMarkupId={focusMarkupId}
                          onFocusMarkup={setFocusMarkupId}
                          selectedMarkupId={selectedMarkupId}
                          onSelectMarkup={setSelectedMarkupId}
                        />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            {!readOnly && (
              <tr
                className="pendenzen__quickrow"
                onBlur={handleQuickRowBlur}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  // Innerhalb des Rich-Text-Editors erzeugt Enter einen neuen
                  // Absatz (Mehrzeiligkeit) statt die Zeile zu speichern – dafür
                  // gibt es dort den "Hinzufügen"-Button/das Verlassen der Zeile.
                  if (e.target.closest(".rte-editable")) return;
                  e.preventDefault();
                  commitQuickAdd();
                }}
              >
                {showCheck && <td className="col-check" />}
                <td className="col-nr" aria-hidden="true">
                  +
                </td>
                <td className="col-art">
                  <select
                    value={quickAdd.typ}
                    onChange={(e) => quickChange("typ", e.target.value)}
                    aria-label="Art"
                    title="Art"
                  >
                    {ART_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.value}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="col-beschreibung">
                  <div className="pendenzen__quickrow-desc">
                    <select
                      value={quickKategorie}
                      onChange={(e) => quickChange("kategorie", e.target.value)}
                      aria-label="Kategorie"
                      title="Kategorie"
                    >
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                    <div className="pendenzen__quickrow-editor">
                      <RichTextEditor
                        key={quickAddEditorKey}
                        value={quickAdd.beschreibung}
                        onChange={(html) => quickChange("beschreibung", html)}
                        placeholder="Neue Pendenz erfassen…"
                      />
                      <button
                        type="button"
                        className="pendenzen__btn pendenzen__btn--primary pendenzen__quickrow-submit"
                        onClick={commitQuickAdd}
                        disabled={isRichTextEmpty(quickAdd.beschreibung) || quickSaving}
                      >
                        {quickSaving ? "Speichern..." : "Hinzufügen"}
                      </button>
                    </div>
                  </div>
                </td>
                <td className="col-verantwortlich">
                  <ResponsibleSelect
                    options={responsibleOptions}
                    selectedIds={quickAdd.verantwortlichIds}
                    onChange={(ids) => quickChange("verantwortlichIds", ids)}
                    onCreateRequest={handleCreateRequest}
                    crmOptions={crmOptions}
                    onLinkExisting={handleLinkExisting}
                    onCommit={() => quickDeadlineRef.current?.focus()}
                    singleOnly
                  />
                </td>
                <td className="col-termin">
                  <input
                    ref={quickDeadlineRef}
                    type="date"
                    value={quickAdd.terminSoll}
                    onChange={(e) => quickChange("terminSoll", e.target.value)}
                    aria-label="Deadline"
                    title="Deadline"
                  />
                </td>
                <td className="col-termin" />
                <td className="col-status">
                  <select
                    value={quickAdd.status}
                    onChange={(e) => quickChange("status", e.target.value)}
                    aria-label="Status"
                    title="Status"
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      )}

      {view === "board" && (
        <div className="pendenzen__board-toolbar">
          <label className="pendenzen__board-groupby">
            <span>Gruppieren nach</span>
            <select
              value={boardGroupBy}
              onChange={(e) => setBoardGroupBy(e.target.value)}
            >
              <option value="kategorie">Kategorie</option>
              <option value="thema">Thema</option>
              <option value="verantwortlich">Zuständigkeit</option>
            </select>
          </label>
          {boardGroupBy !== "kategorie" && (
            <span className="pendenzen__board-hint">
              Gruppierung nach dem ersten{" "}
              {boardGroupBy === "thema" ? "Thema" : "Verantwortlichen"} · Verschieben
              nur bei „Kategorie"
            </span>
          )}
        </div>
      )}

      {view === "board" && (
        <PendenzenBoard
          columns={boardColumns}
          itemsByColumn={boardItems}
          onCardClick={(p) => openEdit(p)}
          onDropCard={
            readOnly || boardGroupBy !== "kategorie"
              ? undefined
              : (cardId, colKey) => {
                  const p = pendenzen.find((x) => x.id === cardId);
                  if (p) handleChangeCategory(p, colKey);
                }
          }
          onAddColumn={
            readOnly || boardGroupBy !== "kategorie" ? undefined : handleAddCategory
          }
          onRenameColumn={
            readOnly || boardGroupBy !== "kategorie" ? undefined : handleRenameCategory
          }
          onDeleteColumn={
            readOnly || boardGroupBy !== "kategorie" ? undefined : handleDeleteCategory
          }
          onAddCard={readOnly ? undefined : handleBoardAddCard}
          renderCard={(p) => (
            <PendenzCard
              pendenz={p}
              onToggleChecklistItem={
                readOnly ? undefined : handleToggleChecklistItem
              }
              hasPlan={(markupsByPendenz[p.id]?.length || 0) > 0}
              onOpenPlan={openPlanViewForPendenz}
            />
          )}
        />
      )}

      {view === "plan" && (
        <PendenzenPlanView
          plansWithVersions={plansWithVersions}
          selectedPlanId={planViewPlanId}
          onSelectPlan={switchPlanView}
          pendenzen={filtered}
          markupsByPendenz={markupsByPendenz}
          project={{ id: projectUuid, code: projectCode }}
          readOnly={readOnly}
          seite={planViewSeite}
          focusMarkupId={planViewFocusMarkupId}
          onFocusPendenz={focusPendenzInPlanView}
          selectedMarkupId={planViewSelectedMarkupId}
          onSelectMarkup={setPlanViewSelectedMarkupId}
          onDataChanged={refreshAfterPlanViewChange}
          onOpenPendenz={openEditById}
          onRequestAssign={setPlanAssignDraft}
          reloadToken={planViewReloadToken}
        />
      )}

      {/* Zuordnungs-/Neuerfassungs-Modal für eine im Plan gezeichnete Markierung */}
      {planAssignDraft && (
        <PlanAssignModal
          draft={planAssignDraft}
          pendenzen={pendenzen}
          project={{ id: projectUuid, code: projectCode }}
          responsibleOptions={responsibleOptions}
          crmOptions={crmOptions}
          onCreateRequest={handleCreateRequest}
          onLinkExisting={handleLinkExisting}
          categories={categories}
          sitzungen={sitzungen}
          themenSuggestions={allThemen}
          onClose={() => setPlanAssignDraft(null)}
          onSaved={async (markup) => {
            setPlanAssignDraft(null);
            await refreshAfterPlanViewChange();
            // Den eingebetteten Viewer neu laden lassen (die Markierung wurde
            // extern angelegt) und die neue Markierung hervorheben.
            setPlanViewReloadToken((t) => t + 1);
            if (markup?.id) {
              setPlanViewSelectedMarkupId(markup.id);
              setPlanViewFocusMarkupId(markup.id);
            }
          }}
        />
      )}

      {/* Bearbeiten-Modal (US-PEND-002) */}
      {editing && editForm && (
        <div
          className="pendenzen__modal-overlay"
          onKeyDown={(e) => {
            if (e.key === "Escape") e.stopPropagation();
          }}
        >
          <div
            className="pendenzen__modal"
            onClick={(e) => e.stopPropagation()}
          >
            <form onSubmit={handleUpdate}>
              <div className="pendenzen__modal-header">
                <h3>
                  Pendenz Nr. {editing.nummer}
                  {readOnly ? " ansehen" : " bearbeiten"}
                </h3>
                <button
                  type="button"
                  className="pendenzen__modal-close"
                  onClick={closeEdit}
                  aria-label="Schliessen"
                >
                  ×
                </button>
              </div>

              <div className="pendenzen__modal-body">
                <fieldset
                  disabled={readOnly}
                  style={{ border: "none", padding: 0, margin: 0, minInlineSize: "auto" }}
                >
                  <PendenzFields
                    form={editForm}
                    onChange={handleEditChange}
                    options={responsibleOptions}
                    onCreateRequest={handleCreateRequest}
                    crmOptions={crmOptions}
                    onLinkExisting={handleLinkExisting}
                    categoryOptions={categories}
                    showStatus
                    sitzungen={sitzungen}
                    onCreateSitzungRequest={
                      readOnly ? undefined : () => setSitzungModal({ target: "edit" })
                    }
                    themenSuggestions={allThemen}
                    readOnly={readOnly}
                  />
                </fieldset>
              </div>

              <div className="pendenzen__modal-footer">
                {!readOnly && (
                  <button
                    type="button"
                    className="pendenzen__btn"
                    onClick={() => {
                      const target = editing;
                      closeEdit();
                      openPlanPicker(target);
                    }}
                    disabled={saving}
                  >
                    Auf Plan verorten
                  </button>
                )}
                {!readOnly && (
                  <button
                    type="button"
                    className="pendenzen__btn pendenzen__btn--danger"
                    onClick={handleDelete}
                    disabled={saving}
                  >
                    Löschen
                  </button>
                )}
                <div className="pendenzen__modal-footer-right">
                  <button
                    type="button"
                    className="pendenzen__btn"
                    onClick={closeEdit}
                    disabled={saving}
                  >
                    {readOnly ? "Schliessen" : "Abbrechen"}
                  </button>
                  {!readOnly && (
                    <button
                      type="submit"
                      className="pendenzen__btn pendenzen__btn--primary"
                      disabled={saving}
                    >
                      {saving ? "Speichern..." : "Speichern"}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Teilen-Dialog (US-PEND-004) */}
      {showShare && (
        <div
          className="pendenzen__modal-overlay"
          onKeyDown={(e) => {
            if (e.key === "Escape") e.stopPropagation();
          }}
        >
          <div
            className="pendenzen__modal pendenzen__modal--share"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pendenzen__modal-header">
              <h3>Pendenzen extern teilen</h3>
              <button
                type="button"
                className="pendenzen__modal-close"
                onClick={() => setShowShare(false)}
                aria-label="Schliessen"
              >
                ×
              </button>
            </div>

            <div className="pendenzen__modal-body">
              <p className="pendenzen__share-hint">
                Wer diesen Link kennt, kann die freigegebenen Pendenzen sehen.
              </p>
              <p className="pendenzen__share-count">
                Aktuell{" "}
                <strong>
                  {externCount} {externCount === 1 ? "Eintrag" : "Einträge"}
                </strong>{" "}
                als extern sichtbar markiert.
              </p>

              {shareError && (
                <div
                  className="pendenzen__error"
                  role="alert"
                  onClick={() => setShareError(null)}
                >
                  {shareError}
                </div>
              )}

              <div className="pendenzen__share-create">
                <label className="pendenzen__field">
                  <span>Ablaufdatum (optional)</span>
                  <input
                    type="date"
                    value={newExpiresAt}
                    onChange={(e) => setNewExpiresAt(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="pendenzen__btn pendenzen__btn--primary"
                  onClick={handleCreateShare}
                  disabled={shareBusy}
                >
                  {shareBusy ? "..." : "Neuen Link erzeugen"}
                </button>
              </div>

              <div className="pendenzen__share-list">
                {shareLoading ? (
                  <div className="pendenzen__share-empty">Links werden geladen...</div>
                ) : shares.length === 0 ? (
                  <div className="pendenzen__share-empty">
                    Noch keine Freigabe-Links vorhanden.
                  </div>
                ) : (
                  shares.map((share) => {
                    const valid = isShareValid(share);
                    const statusLabel = !share.active
                      ? "Widerrufen"
                      : !valid
                      ? "Abgelaufen"
                      : "Aktiv";
                    const statusClass = valid ? "aktiv" : "inaktiv";
                    return (
                      <div
                        key={share.token}
                        className={`pendenzen__share-item pendenzen__share-item--${statusClass}`}
                      >
                        <div className="pendenzen__share-item-main">
                          <span
                            className={`share-status share-status--${statusClass}`}
                          >
                            {statusLabel}
                          </span>
                          <input
                            type="text"
                            className="share-url"
                            value={share.url}
                            readOnly
                            onFocus={(e) => e.target.select()}
                          />
                          <button
                            type="button"
                            className="pendenzen__btn"
                            onClick={() => handleCopyLink(share)}
                          >
                            {copiedToken === share.token ? "Kopiert" : "Kopieren"}
                          </button>
                          {valid && (
                            <button
                              type="button"
                              className="pendenzen__btn pendenzen__btn--danger"
                              onClick={() => handleRevokeShare(share.token)}
                              disabled={shareBusy}
                            >
                              Widerrufen
                            </button>
                          )}
                        </div>
                        <div className="pendenzen__share-item-meta">
                          Erstellt {formatDate(share.createdAt)}
                          {share.createdVon ? ` von ${share.createdVon}` : ""}
                          {share.expiresAt
                            ? ` · läuft ab ${formatDate(share.expiresAt)}`
                            : " · kein Ablaufdatum"}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="pendenzen__modal-footer">
              <div className="pendenzen__modal-footer-right">
                <button
                  type="button"
                  className="pendenzen__btn"
                  onClick={() => setShowShare(false)}
                >
                  Schliessen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CRM-Modal: neue Verantwortliche (Organisation/Person) anlegen & zuordnen */}
      {crmModal && (
        <PendenzResponsibleCreateModal
          type={crmModal.type}
          initialName={crmModal.name}
          partners={partners}
          projectCode={projectCode}
          onClose={() => setCrmModal(null)}
          onCreated={({ type, entity, relation, option }) =>
            handleResponsibleCreated({
              type,
              entity,
              relation,
              addSelect: () => crmModal.addFn?.(option.id),
            })
          }
        />
      )}

      {/* Neue Sitzung direkt aus dem Pendenz-Formular anlegen (US-SITZ-001) */}
      {sitzungModal && (
        <SitzungQuickCreateModal
          projectUuid={projectUuid}
          sitzungen={sitzungen}
          onClose={() => setSitzungModal(null)}
          onCreated={(sitzung) => {
            setSitzungen((prev) => [sitzung, ...prev]);
            if (sitzungModal.target === "form") {
              handleFormChange("sitzungId", sitzung.id);
            } else {
              handleEditChange("sitzungId", sitzung.id);
            }
            setSitzungModal(null);
          }}
        />
      )}

      {/* Zuordnungs-Modal: bestehenden CRM-Eintrag mit dem Projekt verbinden */}
      {linkModal && (
        <PendenzResponsibleLinkModal
          option={linkModal.option}
          projectCode={projectCode}
          onClose={() => setLinkModal(null)}
          onLinked={handleLinked}
        />
      )}

      {/* Personen dem Projekt zuordnen (Bearbeitungsrechte, wie Controlling) */}
      {showMembers && (
        <PendenzMembersModal
          projectCode={projectCode}
          projectTitle={projectTitle}
          onClose={() => setShowMembers(false)}
          onChanged={async () => {
            try {
              setAssignments(await getProjectAssignments(projectCode));
            } catch {
              /* ignore */
            }
          }}
        />
      )}

      {showBulkEdit && (
        <BulkEditModal
          count={bulkSelected.size}
          responsibleOptions={responsibleOptions}
          crmOptions={crmOptions}
          onCreateRequest={handleCreateRequest}
          onLinkExisting={handleLinkExisting}
          categories={categories}
          sitzungen={sitzungen}
          themenSuggestions={allThemen}
          saving={saving}
          onClose={() => setShowBulkEdit(false)}
          onApply={handleBulkApply}
        />
      )}

      {/* "Auf Plan verorten" direkt aus der Liste: Plan auswählen (bei >1 Plan) */}
      {pickerPendenz && (
        <PlanPickerModal
          plansWithVersions={plansWithVersions}
          onSelect={({ plan, versions }) => {
            setVerortungTarget({ pendenz: pickerPendenz, plan, versions });
            setPickerPendenz(null);
          }}
          onClose={() => setPickerPendenz(null)}
        />
      )}

      {/* Board-Card "Plan" bei mehreren Plänen: Plan auswählen */}
      {boardPlanPickerPendenz && (
        <PlanPickerModal
          plansWithVersions={plansWithVersions.filter(({ plan }) =>
            (markupsByPendenz[boardPlanPickerPendenz.id] || []).some(
              (m) => m.planId === plan.id
            )
          )}
          onSelect={({ plan }) => {
            jumpToPendenzOnPlan(boardPlanPickerPendenz, plan.id);
            setBoardPlanPickerPendenz(null);
          }}
          onClose={() => setBoardPlanPickerPendenz(null)}
        />
      )}

      {/* Pläne verwalten (Liste/Upload/Versionen), als Popup direkt aus der Pendenzenliste */}
      {showPlaeneModal && (
        <div
          className="pendenzen__modal-overlay"
          onKeyDown={(e) => {
            if (e.key === "Escape") e.stopPropagation();
          }}
        >
          <div
            className="pendenzen__modal pendenzen__modal--wide"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pendenzen__modal-header">
              <h3>Pläne</h3>
              <button
                type="button"
                className="pendenzen__modal-close"
                onClick={closePlaeneModal}
                aria-label="Schliessen"
              >
                ×
              </button>
            </div>
            <div className="pendenzen__modal-body pendenzen__modal-body--plaene">
              <Plans project={{ id: projectUuid, code: projectCode }} readOnly={readOnly} />
            </div>
          </div>
        </div>
      )}

      {/* "Neue Planpendenz": zuerst markieren, dann Pendenz-Angaben daneben erfassen */}
      {showNeuePlanpendenz && (
        <NeuePlanpendenzModal
          plansWithVersions={plansWithVersions}
          project={{ id: projectUuid, code: projectCode }}
          responsibleOptions={responsibleOptions}
          crmOptions={crmOptions}
          onCreateRequest={handleCreateRequest}
          onLinkExisting={handleLinkExisting}
          categories={categories}
          sitzungen={sitzungen}
          onCreateSitzungRequest={() => setSitzungModal({ target: "form" })}
          themenSuggestions={allThemen}
          onClose={() => setShowNeuePlanpendenz(false)}
          onCreated={() => loadData()}
        />
      )}

      {/* "Auf Plan verorten": Viewer mit fest zugeordneter Pendenz */}
      {verortungTarget && (
        <PlanViewer
          plan={verortungTarget.plan}
          versions={verortungTarget.versions}
          project={{ id: projectUuid, code: projectCode }}
          fixedPendenzId={verortungTarget.pendenz.id}
          fixedPendenzLabel={`Nr. ${verortungTarget.pendenz.nummer} – ${verortungTarget.pendenz.beschreibung}`}
          onClose={closeVerortung}
        />
      )}
    </div>
  );
};

export default Pendenzen;
